import type { QueryClient } from "@tanstack/react-query";
import type {
  BillboardView,
  BootstrapResponse,
  CatalogResponse,
  HomeStateResponse,
  InteriorBoard,
  InteriorThemeUpdateResponse,
  InventoryResponse,
  MyHomeResponse,
  PlotPurchaseResponse,
  PropertyPublic,
  RealEstateListingsResponse,
  RoomTarget,
  RoomTicketResponse,
  SeatResult,
  VehicleResult,
  WalletsResponse,
  WorldManifest,
} from "@townsy/contracts";
import { CLOSE_CODES, worldCopyName } from "@townsy/contracts";
import { districtOfProperty, driverSeatPoint, getPlot, mayUseSeat, nearestEntrance, vehicleObb, type DriveEnv, type Seat } from "@townsy/world";
import { billboardsForScene, billboardsKey } from "../advertise/media";
import { createAnalytics, type Analytics } from "../analytics";
import { errorMessage } from "../api/client";
import { fetchCatalog, fetchProperty, fetchRealEstateListings, markPropertyStatsStale, postRoomTicket } from "../api/endpoints";
import { queryKeys } from "../api/queries";
import type { PreviewSink } from "../homes/placementDraft";
import { InputController } from "../input/inputController";
import { MovementLoop } from "../input/movementLoop";
import { clientMessageId } from "../lib/env";
import { STORAGE_KEYS, readJson, remove, writeJson } from "../lib/storage";
import { describeClose } from "../net/closeCodes";
import { environmentInputs, ownedResidentialIds, predictionEnvironment } from "../net/colliders";
import { createSdkConnector, type Connector } from "../net/connector";
import { DrivePredictor } from "../net/drivePrediction";
import { districtAt, districtName as nameOfDistrict, townDrivableRects } from "../net/townStreet";
import { Predictor, type AuthoritativeState } from "../net/prediction";
import { RealtimeClient, type ReconnectRecord, type ReconnectStorage, type RoomContext } from "../net/realtime";
import { roomSeats, seatOffer } from "../net/seats";
import { ViewFocusSender } from "../net/viewFocus";
import { OUT_OF_CAR_FIRST, carObstacles, enterableVehicle, vehicleResultMessage, vehicleVisual, VEHICLE_RESULT_TEXT, type VehicleSnapshot } from "../net/vehicles";
import type { ActorPostureVisual, ActorVisual, CreateTownScene, DecorationPreview, InteriorView, SceneCallbacks, SignVisibilitySample, TownScene } from "../scene/types";
import { isMovementModalOpen, useTownStore, type CameraMode, type QualityLevel, type UiState } from "../state/store";
import {
  buildInteriorView,
  civicKindOf,
  findCivicId,
  findRealEstateOfficeId,
  interiorBoardKey,
  interiorHomeKey,
  interiorRoomKey,
  interiorThemeKey,
  isRealEstateOfficePlot,
  placementBrandingKey,
  showroomShop,
  type CivicKind,
} from "./interiorView";
import { installTestHook, type HookDrawnCars } from "./testHook";
import { VENUE_CLIENTS, useVenueStore, venueOf, type VenueDriver, type VenueDrivingModule } from "../venues";

/** Drive environment before the walking predictor exists (never mutated). */
const NO_COLLIDERS: readonly never[] = [];

/** A moderation notice within this window already explained a server move: no second toast. */
const NOTICE_DEDUPE_MS = 5_000;

/**
 * One visit to the town: owns the renderer handle, the realtime session, prediction, the input
 * loop and analytics, and keeps the zustand store in sync. React components stay thin: they
 * read the store and call methods here. Nothing in this class decides authority.
 */
export interface TownSessionDeps {
  bootstrap: BootstrapResponse;
  manifest: WorldManifest;
  queryClient: QueryClient;
  navigate(path: string): void;
  /**
   * Seams for unit tests (the app never passes them): the realtime transport, the room-ticket request, the reconnection
   * record store and the renderer module. Defaults: the Colyseus SDK, `POST /room-tickets`, session storage and the
   * lazily loaded Babylon.js chunk.
   */
  connector?: Connector;
  requestTicket?: (target: RoomTarget) => Promise<RoomTicketResponse>;
  reconnectStorage?: ReconnectStorage;
  loadRenderer?: () => Promise<{ createTownScene: CreateTownScene }>;
  /**
   * A district the page was opened for (`/?district=<id>`, a link to that district's square): the first street join
   * lands there instead of the visitor's last district or a spread arrival. Unknown ids are ignored.
   */
  arrivalDistrictId?: string | null;
}

/** ADR-0043: how often the session looks at the Overview camera for `view.focus` (sends are throttled further). */
const VIEW_FOCUS_POLL_MS = 125;

/**
 * World v5 (ADR-0041, kept by ADR-0042 decision 5): the district a visit starts in: the bootstrap's `startDistrictId`
 * (the district the visitor was last in) when the manifest knows it, else the manifest's first district (Central). The
 * first street join asks for that district's square.
 */
export function startDistrictId(bootstrap: Pick<BootstrapResponse, "startDistrictId">, manifest: Pick<WorldManifest, "districts">): string {
  const id = bootstrap.startDistrictId;
  return id && manifest.districts.some((d) => d.id === id) ? id : manifest.districts[0]!.id;
}

export const RETURN_TO_PLAZA_COOLDOWN_MS = 10_000;
/** An Enter/Exit button request that gets neither a ticket nor a refusal stops blocking the UI. */
export const INTERACTION_TIMEOUT_MS = 8_000;
/** A get in / get out request without a vehicle.result stops blocking the UI (getting out first brakes the car). */
export const VEHICLE_RESULT_TIMEOUT_MS = 8_000;
/** A sit / lie / stand request without a seat.result stops blocking the prompt (ADR-0018). */
export const SEAT_RESULT_TIMEOUT_MS = 6_000;
/** Seat requests are spaced at least this far apart (the server accepts one per 300 ms per connection). */
export const SEAT_REQUEST_INTERVAL_MS = 350;
/** Drawn seat height when the local seat is not in the list (yet): a chair, or a bed. */
const FALLBACK_SEAT_Y = { sit: 0.47, lie: 0.62 } as const;
/** Touch devices have no keys: the help names the joystick. */
export const DRIVING_HELP_TOUCH = "You're driving. Joystick up: gas · down: brake/reverse · sideways: steer. Get out with the button at the top.";

/** What the town map draws each frame (read-only copies; positions never enter the zustand store). */
export interface MinimapCar {
  vehicleId: string;
  x: number;
  z: number;
  heading: number;
  halfLength: number;
  halfWidth: number;
  /** Someone drives it (another visitor), or it is the local player's car. */
  driven: boolean;
  isLocal: boolean;
}

export interface MinimapSnapshot {
  /** The local avatar (on foot, or in the driver's seat) on the street; null before the first state / inside. */
  local: { x: number; z: number; heading: number } | null;
  /** The car the local player drives (predicted pose), or null. */
  driving: MinimapCar | null;
  cars: MinimapCar[];
}

export class TownSession {
  readonly realtime: RealtimeClient;
  readonly analytics: Analytics;
  readonly input: InputController;
  /** The civic Real Estate office (world v2: "v-01"), or null if the manifest has none. */
  readonly realEstateOfficeId: string | null;
  /** Townsy Home Builders ("v-02") and Townsy Furnishings ("v-03"), or null if absent. */
  readonly homeBuildersId: string | null;
  readonly furnishingsId: string | null;
  private readonly loop: MovementLoop;
  private scene: TownScene | null = null;
  private predictor: Predictor | null = null;
  private readonly actorVisuals = new Map<string, ActorVisual>();
  private localVisual: ActorVisual;
  private disposed = false;
  private tickCount = 0;
  /** The local-only test hook's `onLocalTick` listeners (e2e walk helpers); empty outside local e2e runs. */
  private readonly localTickListeners = new Set<(p: { x: number; z: number }) => void>();
  private lastFps = -1;
  private readonly store = useTownStore;
  private readonly cleanups: Array<() => void> = [];
  /** Identity of the collider set prediction uses (owned homes + yard items, or a home's furniture). */
  private colliderKey = "";
  /** Home interior (look + furniture) last handed to the renderer. */
  private shownHomeKey: string | null = null;
  private shownHome: { propertyId: string; home: InteriorView["home"] } | null = null;
  /** Uncommitted placement ghost and anchor markers currently requested from the renderer. */
  private decorationPreview: DecorationPreview | null = null;
  private anchorHighlight: { propertyId: string; scope: "yard" | "interior"; anchorIds: string[] } | null = null;
  /** Reason the server gave for moving us (shown once the move completes). */
  private pendingNotice: string | null = null;
  /** ADR-0019: closed homes -> owner actor id, recomputed only when the store's properties change. */
  private closedHomesCache: { properties: unknown; owners: ReadonlyMap<string, string | null> } | null = null;
  private lastModerationAt = 0;
  /** Snap the walk camera behind the avatar once the next membership's first state is applied. */
  private snapPending = true;
  private cameraSnaps = 0;
  /** What the renderer currently shows (avoids rebuilding the room on unrelated property updates). */
  private shownRoomKey: string | null = null;
  private shownBoardKey: string | null = null;
  /** Board / listings last handed to the renderer inside a room (diagnostics for the local test hook). */
  private shownBoard: { propertyId: string; board: InteriorView["board"]; listings: InteriorView["listings"] } | null = null;
  /** "Go there" after a purchase: once back on the street, frame this property in overview. */
  private pendingFocusId: string | null = null;
  /** A district's framing asked for from inside a building (town v6): shown in Overview once back on the street. */
  private pendingDistrictFrame: string | null = null;
  private interactionTimer: ReturnType<typeof setTimeout> | null = null;
  /** Room membership (Colyseus session id) the current predictor was seeded for. */
  private predictorSessionId: string | null = null;
  /**
   * Town v6 (ADR-0042 decision 1): the district the visitor is in: on the street, the district of the local avatar's
   * position (`districtOfPoint`, updated as they walk or drive; between districts' bounds the last one is kept); inside
   * a building, the building's district. The store mirrors it as `districtId`. Starts as the bootstrap's start district.
   */
  private currentDistrictId: string;
  /** The session id of the last room membership `onRoomJoined` saw (see there). */
  private lastJoinedSessionId: string | null = null;
  /** Shared town cars of the current street room (latest authoritative state, ADR-0006). */
  private readonly vehicles = new Map<string, VehicleSnapshot>();
  /** The car the local player drives (ActorState.vehicleId), or null while walking. */
  private drivingVehicleId: string | null = null;
  private drive: DrivePredictor | null = null;
  /** A venue vehicle the local player drives (ADR-0016 truck arena): the venue's own prediction. */
  private venueDrive: VenueDriver | null = null;
  /** Highest input sequence number used while driving in this membership (one increasing sequence per actor). */
  private driveSeqFloor = 0;
  private doorHighlightId: string | null = null;
  private vehicleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Store theme last handed to the renderer inside a store (live swaps use updateInteriorStoreTheme). */
  private shownThemeKey: string | null = null;
  private lastTransition: { roomName: string; propertyId: string | null; districtId: string | null; serverInitiated: boolean; at: number } | null = null;
  /** What the four billboards show today (GET /billboards, ADR-0009 #9), kept for a renderer that starts later. */
  private billboards: BillboardView[] | null = null;
  private shownBillboardsKey: string | null = null;
  /**
   * The current room's seats (ADR-0018: the shared packages/world list the server seats people from), by id, and the
   * identity of the data they were listed from.
   */
  private seatList: Seat[] = [];
  private seatsById = new Map<string, Seat>();
  private seatsKey = "";
  /** The seat the local avatar is on (the authoritative ActorState.posture / seatId), or null when standing. */
  private localSeat: { kind: "sit" | "lie"; seatId: string } | null = null;
  /** A movement key was released since sitting down: holding W while sitting down must not stand up again at once. */
  private seatedMoveArmed = false;
  private seatTimer: ReturnType<typeof setTimeout> | null = null;
  private lastSeatRequestAt = 0;
  /** ADR-0043: the Overview camera focus told to the street room (throttled), and the membership it was told in. */
  private readonly viewFocus = new ViewFocusSender((focus) => this.realtime.sendViewFocus(focus));
  private viewFocusSession: string | null = null;
  /**
   * ADR-0043 decision 2: the first street join asked for no district (a new visitor): once its first state says where
   * the server landed them, the current district follows the avatar there. The Overview stays on Home (founder
   * 2026-10-26: the town always opens in Overview over the city centre; Walk takes the visitor to wherever they landed).
   */
  private arrivalDistrictPending = false;

  constructor(private readonly deps: TownSessionDeps) {
    const { bootstrap, manifest } = deps;
    const actor = bootstrap.actor;
    this.currentDistrictId = startDistrictId(bootstrap, manifest);
    this.store.getState().setDistrictId(this.currentDistrictId);
    this.realEstateOfficeId = findRealEstateOfficeId(manifest);
    this.homeBuildersId = findCivicId(manifest, "home_builders");
    this.furnishingsId = findCivicId(manifest, "furnishings");
    this.localVisual = {
      actorId: actor.actorId,
      label: actor.label,
      countryCode: actor.countryCode,
      appearance: actor.appearance,
      x: 0,
      z: 0,
      facing: 0,
      anim: 0,
      isLocal: true,
    };
    this.realtime = new RealtimeClient({
      connector: deps.connector ?? createSdkConnector(window.location.origin + (bootstrap.realtime.endpointPath || "/realtime")),
      requestTicket: deps.requestTicket ?? ((target) => postRoomTicket(target)),
      storage: deps.reconnectStorage ?? {
        load: () => readJson<ReconnectRecord>("session", STORAGE_KEYS.reconnect),
        save: (r) => writeJson("session", STORAGE_KEYS.reconnect, r),
        clear: () => remove("session", STORAGE_KEYS.reconnect),
      },
      actorId: actor.actorId,
      // Driving messages go to the street and to venues with vehicles (the truck arena), never to other buildings.
      drivableInterior: (propertyId) => {
        const v = venueOf(manifest, propertyId);
        return v !== null && Boolean(VENUE_CLIENTS[v].driving);
      },
      // Seated actors are drawn at their seat's height (ADR-0018).
      resolveSeat: (seatId) => this.seatsById.get(seatId) ?? null,
      defaults: {
        simulationHz: manifest.movement.simulationHz,
        chatDwellSeconds: bootstrap.limits.chatGuestDwellSeconds,
        chatMinIntervalMs: bootstrap.session.userId ? bootstrap.limits.chatRegisteredIntervalMs : bootstrap.limits.chatGuestIntervalMs,
      },
    });
    // Civic buildings are Townsy-run, not advertising: never statistics candidates (ADR-0004 #6).
    this.analytics = createAnalytics({ isMeasurable: (id) => getPlot(manifest, id)?.type !== "CIVIC" });
    this.input = new InputController({
      getCameraYaw: () => this.scene?.getCameraYaw() ?? 0,
      isWalkMode: () => this.store.getState().ui.mode === "walk",
      // Includes the dollar checkout: its card fields are in Stripe's iframe (A09).
      isModalOpen: () => isMovementModalOpen(this.store.getState()),
      isChatFocused: () => this.store.getState().ui.chatFocused,
      onInteract: () => this.interact(),
      isRunToggled: () => this.store.getState().ui.runToggle,
    });
    this.loop = new MovementLoop({
      hz: manifest.movement.simulationHz,
      getIntent: () => this.input.getIntent(),
      // Never walk while driving: the server ignores input.move then.
      getPredictor: () => (this.drivingVehicleId ? null : this.predictor),
      send: (input) => {
        this.realtime.sendInput(input);
      },
      onTick: (state, moving) => this.onLocalTick(state, moving),
      getDrive: () => (this.drivingVehicleId ? (this.drive ?? this.venueDrive) : null),
      getDriveIntent: () => this.input.getDriveIntent(),
      sendDrive: (input) => {
        this.realtime.sendDrive(input);
      },
      onDriveTick: () => this.onDriveTick(),
      // ADR-0018: Shift / the Run toggle sets the run bit; nothing is predicted while sitting or lying.
      getRun: () => this.input.isRunning(),
      isSeated: () => this.localSeat !== null,
      onSeated: (moving) => this.onSeatedTick(moving),
    });
    this.wireRealtime();
    this.watchListings();
    this.input.attach();
    // ADR-0043: tell the street room where the Overview camera looks (pan, zoom, district framing, Home), throttled.
    const focusTimer = setInterval(() => this.pollViewFocus(), VIEW_FOCUS_POLL_MS);
    this.cleanups.push(() => clearInterval(focusTimer));
    const onVisibility = () => this.realtime.sendPresenceHint(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibility);
    this.cleanups.push(() => document.removeEventListener("visibilitychange", onVisibility));
    const onResize = () => this.scene?.resize();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    this.cleanups.push(() => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    });
    // Local development only: read-only diagnostics for the e2e suite (see ./testHook.ts).
    if (bootstrap.environment === "local") this.cleanups.push(installTestHook(this.testHookSource()));
  }

  /** Snapshot providers for the local-only `window.__townsy` hook; nothing here mutates state. */
  private testHookSource() {
    const S = () => this.store.getState();
    const qc = this.deps.queryClient;
    const diag = () => this.scene as (TownScene & { sampleSigns?: () => SignVisibilitySample[]; fitReport?: unknown; debug?: () => SceneDebug; drawnCars?: () => HookDrawnCars }) | null;
    // Renderer internals exposed by createTownScene's harness-only `debug()` (read, never written).
    type SceneDebug = {
      overview?: { framing(): { radius: number; alpha: number; beta: number; target: { x: number; y: number; z: number } } };
      street?: { plotHeight(plotId: string): number };
      overviewDistrict?: string | null;
      overviewBounds?: { minX: number; minZ: number; maxX: number; maxZ: number };
      overviewInput?: string | null;
      districtFramings?: Record<string, { alpha: number; beta: number; radius: number; target: { x: number; y: number; z: number } }>;
      logos?: Array<{ key: string; business: string; logo: string; reason: string | null; refs: number }>;
      billboards?: { boards: Array<{ id: string; content: string; shown: string; pending: string | null; near: boolean; image: { url: string; status: string; reason: string | null } | null }> };
      actors?: {
        speechDiagnostics(actorId: string): {
          text: string;
          bubble: { cx: number; cy: number; heightPx: number } | null;
          rect: { left: number; top: number; right: number; bottom: number } | null;
          tag: { cx: number; cy: number; heightPx: number } | null;
          ai: boolean;
        } | null;
      };
    };
    const toHookActor = (v: ActorVisual) => ({
      actorId: v.actorId,
      label: v.label,
      x: v.x,
      z: v.z,
      facing: v.facing,
      anim: v.anim,
      isLocal: v.isLocal,
      posture: v.posture ? (v.posture.kind === "lie" ? 2 : 1) : 0,
      seatId: v.posture?.seatId ?? "",
    });
    return {
      actorId: this.deps.bootstrap.actor.actorId,
      status: () => this.realtime.connectionStatus,
      sessionId: () => this.realtime.context?.sessionId ?? null,
      roomId: () => this.realtime.roomId,
      roomKey: () => S().room.roomKey,
      room: () => S().room,
      roomProblem: () => S().roomProblem,
      local: () => (S().localActorId ? toHookActor(this.localVisual) : null),
      localAppearance: () => (S().localActorId ? { ...this.localVisual.appearance } : null),
      actors: () => {
        const localId = S().localActorId;
        return Array.from(this.actorVisuals.values()).map((v) => (v.actorId === localId ? toHookActor(this.localVisual) : toHookActor(v)));
      },
      mode: () => S().ui.mode,
      cameraMode: () => S().ui.mode,
      cameraSnaps: () => this.cameraSnaps,
      lastTransition: () => (this.lastTransition ? { ...this.lastTransition } : null),
      sceneStatus: () => S().ui.sceneStatus,
      cameraYaw: () => this.scene?.getCameraYaw() ?? null,
      fps: () => S().ui.fps,
      signSamples: () => diag()?.sampleSigns?.() ?? null,
      fitReport: () => diag()?.fitReport ?? null,
      chat: () => S().chat.messages,
      wallets: () => qc.getQueryData<WalletsResponse>(queryKeys.wallets) ?? null,
      listings: () => qc.getQueryData<RealEstateListingsResponse>(queryKeys.realEstateListings) ?? null,
      ownedResidential: () => Array.from(ownedResidentialIds(this.allProperties())).sort(),
      appearanceOf: (actorId: string) => {
        if (actorId === S().localActorId) return { ...this.localVisual.appearance };
        const v = this.actorVisuals.get(actorId);
        return v ? { ...v.appearance } : null;
      },
      property: (id: string) => S().properties[id] ?? null,
      interiorBoard: () => (this.shownBoard ? { ...this.shownBoard } : null),
      overviewCamera: () => {
        const f = diag()?.debug?.().overview?.framing();
        return f ? { radius: f.radius, alpha: f.alpha, beta: f.beta, target: { x: f.target.x, y: f.target.y, z: f.target.z } } : null;
      },
      cars: () => Array.from(this.vehicles.values()).map((v) => (v.vehicleId === this.drivingVehicleId && this.drive ? { x: this.drive.state.x, z: this.drive.state.z, speed: this.drive.state.speed } : { x: v.x, z: v.z, speed: v.speed })),
      vehicles: () =>
        Array.from(this.vehicles.values()).map((v) => {
          const local = v.vehicleId === this.drivingVehicleId && this.drive !== null;
          const pose = local ? this.drive!.state : v;
          return { vehicleId: v.vehicleId, style: v.style, color: v.color, mode: v.mode, x: pose.x, z: pose.z, heading: pose.heading, speed: pose.speed, driverActorId: v.driverActorId, isLocal: local };
        }),
      driving: () => {
        const d = this.drive ?? this.venueDrive;
        return this.drivingVehicleId
          ? {
              vehicleId: this.drivingVehicleId,
              pending: S().ui.vehiclePending,
              held: d?.isHeld ?? false,
              pendingCount: d?.pendingCount ?? 0,
              acknowledgedSeq: d?.acknowledgedSeq ?? 0,
              lastCorrection: d?.lastCorrection ?? 0,
              snaps: d?.snaps ?? 0,
            }
          : null;
      },
      vehiclePrompt: () => S().ui.vehicleCandidateId,
      doorHighlight: () => this.doorHighlightId,
      interiorStoreTheme: () => this.shownThemeKey || null,
      buildingHeights: () => {
        // The street the renderer shows is the whole town (ADR-0042): every shop's height.
        const street = diag()?.debug?.().street;
        const out: Record<string, number> = {};
        if (street) for (const p of this.deps.manifest.plots) if (p.type === "COMMERCIAL") out[p.id] = street.plotHeight(p.id);
        return out;
      },
      district: () => {
        const d = diag()?.debug?.();
        const ctx = this.realtime.context;
        const lv = this.localVisual;
        return {
          current: this.currentDistrictId,
          store: S().districtId,
          /** The district of the local avatar's position on the street (null inside, or before the first state). */
          byPosition: ctx?.kind === "street" && this.predictor ? districtAt(this.deps.manifest, lv.x, lv.z, "") || null : null,
          /** The district the overview camera looks at now. */
          overview: d?.overviewDistrict ?? null,
        };
      },
      overviewState: () => {
        const d = diag()?.debug?.();
        if (!d?.overviewBounds) return null;
        return { district: d.overviewDistrict ?? null, bounds: { ...d.overviewBounds }, lastInput: d.overviewInput ?? null, framings: d.districtFramings ? JSON.parse(JSON.stringify(d.districtFramings)) : {} };
      },
      viewFocus: () => this.viewFocusState(),
      rendererSigns: () => diag()?.debug?.().logos?.map((l) => ({ key: l.key, business: l.business, logo: l.logo, reason: l.reason, refs: l.refs })) ?? null,
      speech: (actorId: string) => {
        const d = diag()?.debug?.().actors?.speechDiagnostics(actorId) ?? null;
        return d ? { text: d.text, bubble: d.bubble ? { ...d.bubble } : null, rect: d.rect ? { ...d.rect } : null, tag: d.tag ? { ...d.tag } : null, ai: d.ai } : null;
      },
      rendererBillboards: () =>
        diag()
          ?.debug?.()
          .billboards?.boards.map((b) => ({ id: b.id, content: b.content, shown: b.shown, pending: b.pending, near: b.near, image: b.image ? { ...b.image } : null })) ?? null,
      venue: () => {
        const v = useVenueStore.getState();
        return { venue: v.venue, seq: v.seq, shared: v.shared, mine: v.mine, chips: v.chips, events: v.events.map((e) => ({ type: e.type, code: e.code ?? null, message: e.message })) };
      },
      drawnCars: () => diag()?.drawnCars?.() ?? null,
      onLocalTick: (listener: (p: { x: number; z: number }) => void) => {
        this.localTickListeners.add(listener);
        return () => {
          this.localTickListeners.delete(listener);
        };
      },
      seating: () => ({
        seatCount: this.seatList.length,
        offer: S().ui.seatOffer,
        seated: S().ui.seated,
        pending: S().ui.seatPending,
        running: this.input.isRunning(),
        seats: this.seatList.map((x) => ({ id: x.id, kind: x.kind, x: x.x, z: x.z, place: x.place })),
      }),
      ui: () => {
        const ui = S().ui;
        return {
          selectedPropertyId: ui.selectedPropertyId,
          nearbyEntranceId: ui.nearbyEntranceId,
          transitionPending: ui.transitionPending,
          chatFocused: ui.chatFocused,
          chatOpen: ui.chatOpen,
          statsOpen: ui.statsOpen,
          walletOpen: ui.walletOpen,
          realEstateOpen: ui.realEstateOpen,
          homeBuildersOpen: ui.homeBuildersOpen,
          furnishingsOpen: ui.furnishingsOpen,
          myHomeOpen: ui.myHomeOpen,
          myHomeTab: ui.myHomeTab,
          earnOpen: ui.earnOpen,
          storeTasksFor: ui.storeTasksFor,
          taskOpen: S().task !== null,
        };
      },
      myHome: () => qc.getQueryData<MyHomeResponse>(queryKeys.myHome)?.home ?? null,
      inventory: () => qc.getQueryData<InventoryResponse>(queryKeys.inventory)?.items ?? null,
      catalog: () => qc.getQueryData<CatalogResponse>(queryKeys.catalog) ?? null,
      homeLook: (propertyId?: string) => {
        const id = propertyId ?? qc.getQueryData<MyHomeResponse>(queryKeys.myHome)?.home?.propertyId ?? null;
        return id ? (S().properties[id]?.home?.look ?? null) : null;
      },
      decorationPreview: () => this.decorationPreview,
      highlightedAnchors: () => this.anchorHighlight,
      interiorHome: () => this.shownHome,
      prediction: () =>
        this.predictor
          ? {
              pendingCount: this.predictor.pendingCount,
              acknowledgedSeq: this.predictor.acknowledgedSeq,
              lastCorrection: this.predictor.lastCorrection,
              colliderCount: this.predictor.colliderCount,
            }
          : null,
    };
  }

  /**
   * Town v6 (ADR-0042): the CURRENT district: on the street the district of the local avatar's position (updated as
   * they move), inside a building the building's district (the store mirrors it as `districtId`).
   */
  get districtId(): string {
    return this.currentDistrictId;
  }

  /** The district of a property (its plot's), or null for an id the manifest does not know. */
  districtOf(propertyId: string): string | null {
    return districtOfProperty(this.deps.manifest, propertyId);
  }

  /** A district's display name ("Riverside"). */
  districtName(districtId: string): string {
    return nameOfDistrict(this.deps.manifest, districtId);
  }

  private knownDistrict(id: string | null | undefined): id is string {
    return Boolean(id) && this.deps.manifest.districts.some((d) => d.id === id);
  }

  private setCurrentDistrict(districtId: string): void {
    if (districtId === this.currentDistrictId) return;
    this.currentDistrictId = districtId;
    this.store.getState().setDistrictId(districtId);
  }

  /**
   * The district of a room as it is joined: an interior's plot's; a street's spawn district when the ticket named one
   * the manifest knows (the square asked for: the first join, "Reconnected"), else the current one. On the street the
   * avatar's position decides from its first state on (`followDistrictOf`).
   */
  private roomDistrictOf(ctx: Pick<RoomContext, "kind" | "districtId" | "propertyId"> | null): string {
    if (!ctx) return this.currentDistrictId;
    const id = ctx.kind === "street" ? ctx.districtId : ctx.propertyId ? this.districtOf(ctx.propertyId) : null;
    return this.knownDistrict(id) ? id : this.currentDistrictId;
  }

  /** On the street: the current district follows the local avatar's position (no change between districts' bounds). */
  private followDistrictOf(x: number, z: number): void {
    if (this.realtime.context?.kind !== "street") return;
    this.setCurrentDistrict(districtAt(this.deps.manifest, x, z, this.currentDistrictId));
  }

  /** The world manifest this session was built with (the town map draws from it). */
  get manifest(): WorldManifest {
    return this.deps.manifest;
  }

  isRealEstateOffice(propertyId: string): boolean {
    return isRealEstateOfficePlot(this.deps.manifest, propertyId);
  }

  /** Which Townsy-run building this is (Real Estate, Home Builders, Furnishings), or null. */
  /** Attractions (ADR-0016): send an intent to the game of the venue the visitor is in. */
  sendVenueAction(action: Record<string, unknown>): boolean {
    const venue = useVenueStore.getState().venue;
    if (!venue) return false;
    return this.realtime.sendVenueAction({ venue, action });
  }

  civicKind(propertyId: string): CivicKind | null {
    return civicKindOf(this.deps.manifest, propertyId);
  }

  /** The signed-in resident owns this home (from `GET /me/home`, or the public owner actor id). */
  ownsHome(propertyId: string): boolean {
    if (this.deps.queryClient.getQueryData<MyHomeResponse>(queryKeys.myHome)?.home?.propertyId === propertyId) return true;
    const owner = this.store.getState().properties[propertyId]?.ownerActorId ?? null;
    return owner !== null && owner === this.deps.bootstrap.actor.actorId;
  }

  /**
   * Whether the Enter controls should be offered. The public DTO says `enterable: false` for a home or store its owner
   * closed (ADR-0019), but the owner may still go in (the room-ticket API and the room admit them).
   */
  canEnter(propertyId: string): boolean {
    const property = this.store.getState().properties[propertyId];
    if (property?.enterable) return true;
    if (!property || property.status !== "OWNED") return false;
    if (property.type === "RESIDENTIAL") return Boolean(property.home) && this.ownsHome(propertyId);
    return property.type === "COMMERCIAL" && property.access === "closed" && property.ownerActorId === this.deps.bootstrap.actor.actorId;
  }

  /**
   * ADR-0019: the closed homes of the town -> their owner's actor id, for the seat offer (a closed home's yard seats are
   * its owner's; the server applies the same rule and answers CLOSED).
   */
  private closedHomeOwners(): ReadonlyMap<string, string | null> {
    const properties = this.store.getState().properties;
    if (this.closedHomesCache?.properties === properties) return this.closedHomesCache.owners;
    const owners = new Map<string, string | null>();
    for (const p of Object.values(properties)) {
      if (p.type === "RESIDENTIAL" && p.status === "OWNED" && p.access === "closed") owners.set(p.id, p.ownerActorId);
    }
    this.closedHomesCache = { properties, owners };
    return owners;
  }

  /** True while the local avatar is inside this property's room. */
  isInside(propertyId: string): boolean {
    const ctx = this.realtime.context;
    return ctx?.kind === "interior" && ctx.propertyId === propertyId;
  }

  // ---------------------------------------------------------------- scene

  async startScene(canvas: HTMLCanvasElement): Promise<void> {
    const st = this.store.getState();
    st.setUi({ sceneStatus: "loading", sceneError: null });
    try {
      // The renderer (Babylon.js) is a separate chunk; the app shell never imports it statically.
      const mod = await (this.deps.loadRenderer ?? (() => import("../scene/createTownScene")))();
      if (this.disposed) return;
      // `onSelectVehicle` (a click / tap on a car) is offered ahead of the renderer contract: a
      // renderer that reports car picks gets "tap the car to get in"; one that does not ignores it.
      const callbacks: SceneCallbacks = {
        onSelectProperty: (id) => this.selectFromScene(id),
        onSelectVehicle: (vehicleId) => this.selectVehicleFromScene(vehicleId),
        // A click on a person or their name tag opens their card (ADR-0013); the store ignores the local avatar.
        onSelectActor: (actorId) => this.store.getState().selectActor(actorId),
        onSignVisibility: (samples) => {
          if (this.realtime.context?.kind === "street") this.analytics.ingestSignSamples(samples);
        },
        onReady: () => {},
        onError: (err) => this.onSceneError(err),
        // ADR-0054 / ADR-0055: the simulation steps that fell due run at the start of each frame, whatever is stepped (a
        // setInterval callback can run up to two frames late in a busy page): the driven street car (it was drawn on past
        // a stop and jumped back), the walking avatar (it stood for a frame and jumped on) and a venue vehicle (the
        // arena's truck: the same). The loop's accumulator runs only the steps that are due (normally none, sometimes
        // one, at most 3): the input rate is unchanged. Its interval timer still runs (a hidden tab draws no frames).
        onFrame: () => {
          if (this.loop.running) this.loop.tick();
        },
        onFps: (fps) => {
          const rounded = Math.round(fps);
          if (rounded !== this.lastFps) {
            this.lastFps = rounded;
            this.store.getState().setUi({ fps: rounded });
          }
        },
      };
      // Town v6 (ADR-0042): the whole town is built. Founder 2026-10-26: "When I load Townsy.io, it should always load in
      // 'overview' in the center of the city ... seeing all the building logos on top. If you click 'walk' then that's
      // when it can place you wherever it wants": the overview opens on Home (Central, the first district), whatever
      // district the visitor is in; a /place link still frames its property once the scene is up (focusProperty).
      const scene = await mod.createTownScene(canvas, this.deps.manifest, callbacks, {
        quality: st.ui.quality,
        reducedMotion: st.ui.reducedMotion,
        isTouch: st.ui.isTouch,
        districtId: this.deps.manifest.districts[0]!.id,
        // The overview's arrow / W A S D keys follow the same rules as walking: never with a dialog open or in chat.
        overviewKeysAllowed: () => {
          const s = this.store.getState();
          return !isMovementModalOpen(s) && !s.ui.chatFocused;
        },
      });
      if (this.disposed) {
        scene.dispose();
        return;
      }
      this.scene = scene;
      scene.setPlacements(this.allProperties());
      this.shownBillboardsKey = null;
      this.pushBillboards();
      scene.setMode(this.store.getState().ui.mode);
      for (const v of this.actorVisuals.values()) scene.upsertActor(v);
      const localId = this.store.getState().localActorId;
      if (localId) {
        scene.upsertActor(this.localVisual);
        scene.setLocalActor(localId);
      }
      this.applyRoomToScene(true);
      // Cars that arrived before the renderer (the local car is drawn by the next drive tick).
      for (const v of this.vehicles.values()) if (v.vehicleId !== this.drivingVehicleId) scene.upsertVehicle(vehicleVisual(v, false));
      if (this.drivingVehicleId) scene.setLocalVehicle(this.drivingVehicleId);
      if (this.doorHighlightId) scene.highlightVehicleDoor(this.doorHighlightId);
      // The renderer arrived after the room: frame the avatar the same way a fresh join does.
      this.snapPending = true;
      this.maybeSnapCamera();
      const selected = this.store.getState().ui.selectedPropertyId;
      if (selected) this.focusProperty(selected);
      this.store.getState().setUi({ sceneStatus: "ready" });
    } catch (err) {
      this.onSceneError(err);
    }
  }

  private onSceneError(err: unknown): void {
    console.error("[scene] failed", err);
    const scene = this.scene;
    this.scene = null;
    this.shownRoomKey = null;
    this.shownBoardKey = null;
    this.shownBoard = null;
    this.shownHomeKey = null;
    this.shownHome = null;
    this.decorationPreview = null;
    this.anchorHighlight = null;
    this.shownThemeKey = null;
    this.doorHighlightId = null;
    this.shownBillboardsKey = null;
    try {
      scene?.dispose();
    } catch {
      /* ignore */
    }
    this.store.getState().setUi({ sceneStatus: "failed", sceneError: errorMessage(err, "The 3D view could not start.") });
  }

  private allProperties(): PropertyPublic[] {
    const s = this.store.getState();
    return s.propertyIds.map((id) => s.properties[id]).filter((p): p is PropertyPublic => Boolean(p));
  }

  /**
   * What each billboard shows today, from `GET /billboards` (the server computes "today" in UTC, so a
   * delayed worker never shows the wrong creative). Only approved same-origin image URLs reach the
   * renderer; it is called again only when something changed.
   */
  setBillboards(items: BillboardView[]): void {
    this.billboards = billboardsForScene(items);
    this.pushBillboards();
  }

  private pushBillboards(): void {
    const scene = this.scene;
    const items = this.billboards;
    if (!scene || !items) return;
    const key = billboardsKey(items);
    if (key === this.shownBillboardsKey) return;
    try {
      scene.setBillboards(items);
      this.shownBillboardsKey = key;
    } catch (err) {
      console.warn("[scene] setBillboards failed", err);
    }
  }

  /** Push the latest public property list to the renderer and prediction (called when the store list changes). */
  updatePlacements(): void {
    this.scene?.setPlacements(this.allProperties());
    this.refreshPredictionColliders();
    this.refreshSeats();
    this.applyRoomToScene();
  }

  /**
   * Owned homes or placed items changed (a purchase, a placement, a removal anywhere): rebuild the
   * prediction colliders of the current room the same way the realtime server does, keeping
   * pending inputs. Street: owned homes + yard items; a home's room: its furniture.
   */
  private refreshPredictionColliders(): void {
    const ctx = this.realtime.context;
    if (!this.predictor || !ctx) return;
    const inputs = environmentInputs(ctx, this.allProperties());
    if (inputs.key === this.colliderKey) return;
    this.colliderKey = inputs.key;
    const env = predictionEnvironment(this.deps.manifest, ctx, inputs.owned, inputs.decor);
    this.predictor.setColliders(env.colliders);
  }

  private listingsData(): RealEstateListingsResponse | null {
    return this.deps.queryClient.getQueryData<RealEstateListingsResponse>(queryKeys.realEstateListings) ?? null;
  }

  private catalogData(): CatalogResponse | null {
    return this.deps.queryClient.getQueryData<CatalogResponse>(queryKeys.catalog) ?? null;
  }

  private forgetShownRoom(): void {
    this.shownBoardKey = null;
    this.shownBoard = null;
    this.shownThemeKey = null;
    this.shownHomeKey = null;
    this.shownHome = null;
    // A rebuilt room drops any ghost or markers; the My home manager re-sends them for the new room.
    this.decorationPreview = null;
    this.anchorHighlight = null;
  }

  /**
   * Show the current room. The room is rebuilt only when its identity/branding changed; the store
   * board and the office listings are updated in place (`updateInteriorBoard`).
   */
  private applyRoomToScene(force = false): void {
    const ctx = this.realtime.context;
    const scene = this.scene;
    if (!scene || !ctx) return;
    if (ctx.kind === "street") {
      // Town v6 (ADR-0042): one street, the whole town; nothing changes when the visitor crosses into another district.
      const key = "street";
      if (force || this.shownRoomKey !== key) {
        scene.showStreet();
        this.shownRoomKey = key;
        this.forgetShownRoom();
        this.analytics.resetSigns();
      }
      return;
    }
    if (!ctx.propertyId) return;
    const view = buildInteriorView(
      this.deps.manifest,
      ctx.propertyId,
      this.store.getState().properties[ctx.propertyId],
      this.listingsData(),
      this.catalogData(),
    );
    if (!view) return;
    const roomKey = interiorRoomKey(view);
    const boardKey = interiorBoardKey(view);
    const homeKey = interiorHomeKey(view);
    if (force || roomKey !== this.shownRoomKey) {
      this.forgetShownRoom();
      scene.showInterior(view);
      this.shownRoomKey = roomKey;
      this.shownBoardKey = boardKey;
      this.shownBoard = { propertyId: view.propertyId, board: view.board, listings: view.listings };
      this.shownHomeKey = homeKey;
      this.shownHome = view.home ? { propertyId: view.propertyId, home: view.home } : null;
      this.shownThemeKey = interiorThemeKey(view);
      this.analytics.resetSigns();
      return;
    }
    const themeKey = interiorThemeKey(view);
    if (themeKey !== this.shownThemeKey && view.storeTheme) {
      // The owner picked another store theme (property.changed): swap it in place for everyone inside.
      try {
        scene.updateInteriorStoreTheme(view.storeTheme);
      } catch (err) {
        console.warn("[scene] updateInteriorStoreTheme failed", err);
      }
      this.shownThemeKey = themeKey;
    }
    if (boardKey !== this.shownBoardKey) {
      scene.updateInteriorBoard(view.board, view.listings);
      this.shownBoardKey = boardKey;
      this.shownBoard = { propertyId: view.propertyId, board: view.board, listings: view.listings };
    }
    if (homeKey !== this.shownHomeKey) {
      // A home's look or furniture changed (property.changed): update in place, never rebuild the room.
      try {
        scene.updateInteriorHome(view.home);
      } catch (err) {
        console.warn("[scene] updateInteriorHome failed", err);
      }
      this.shownHomeKey = homeKey;
      this.shownHome = view.home ? { propertyId: view.propertyId, home: view.home } : null;
    }
  }

  /**
   * Keep the boards in sync with every fetch: the office listings (panel refetch, purchase, poll)
   * and the showroom catalog highlights (Home Builders / Furnishings).
   */
  private watchListings(): void {
    const cache = this.deps.queryClient.getQueryCache();
    const off = cache.subscribe((event) => {
      if (event.type !== "updated") return;
      const key = event.query.queryKey[0];
      const ctx = this.realtime.context;
      if (ctx?.kind !== "interior" || !ctx.propertyId) return;
      const kind = this.civicKind(ctx.propertyId);
      if (key === queryKeys.realEstateListings[0] && kind === "real_estate_office") this.applyRoomToScene();
      else if (key === queryKeys.catalog[0] && showroomShop(kind)) this.applyRoomToScene();
    });
    this.cleanups.push(off);
  }

  private prefetchListings(): void {
    void this.deps.queryClient.prefetchQuery({ queryKey: queryKeys.realEstateListings, queryFn: fetchRealEstateListings, staleTime: 5_000 });
  }

  private prefetchCatalog(): void {
    void this.deps.queryClient.prefetchQuery({ queryKey: queryKeys.catalog, queryFn: fetchCatalog, staleTime: 30_000 });
  }

  /** Snap once the right room is shown AND the local avatar has its first authoritative position. */
  private maybeSnapCamera(): void {
    if (!this.snapPending || !this.scene || !this.predictor) return;
    if (this.realtime.connectionStatus !== "joined") return;
    this.snapPending = false;
    try {
      this.scene.snapCameraBehindLocalActor();
      this.cameraSnaps++;
    } catch (err) {
      console.warn("[scene] snapCameraBehindLocalActor failed", err);
    }
  }

  // ---------------------------------------------------------------- realtime

  /**
   * First join: the town's street, at the square of the current district (the bootstrap's start district: the one the
   * visitor was last in), or of the property's district when the page was opened at `/place/:id`. A saved reconnection
   * token restores the room it was for instead.
   */
  async connect(): Promise<void> {
    if (this.disposed) return;
    const target = this.firstStreetDistrict();
    // ADR-0043 decision 2: a new visitor asks for no district; the street room lands them on an arrival square, and the
    // current district follows them there once their first state arrives (the Overview stays on Home).
    this.arrivalDistrictPending = target === null;
    if (target) this.setCurrentDistrict(target);
    await this.realtime.joinStreet(target ?? undefined, { reconnectFirst: true });
  }

  /**
   * The square the first street join asks for: the property's district at `/place/:id`; the district of a
   * `/?district=<id>` link; the district the visitor was last in (the bootstrap's start district); else none (a new
   * visitor: spread arrival, ADR-0043 decision 2).
   */
  private firstStreetDistrict(): string | null {
    const selected = this.store.getState().ui.selectedPropertyId;
    const ofPlace = selected ? this.districtOf(selected) : null;
    if (ofPlace) return ofPlace;
    if (this.knownDistrict(this.deps.arrivalDistrictId)) return this.deps.arrivalDistrictId;
    if (this.knownDistrict(this.deps.bootstrap.startDistrictId)) return this.deps.bootstrap.startDistrictId;
    return null;
  }

  /**
   * ADR-0043: every poll, the Overview camera's ground focus (null when walking, inside a building or without a 3D view),
   * handed to the throttled sender; a new street membership starts over (the room knows no focus yet).
   */
  private pollViewFocus(): void {
    if (this.disposed) return;
    const ctx = this.realtime.context;
    if (ctx?.kind !== "street" || this.realtime.connectionStatus !== "joined") {
      this.viewFocus.reset();
      this.viewFocusSession = null;
      return;
    }
    if (ctx.sessionId !== this.viewFocusSession) {
      this.viewFocus.reset();
      this.viewFocusSession = ctx.sessionId;
    }
    const focus = this.store.getState().ui.mode === "overview" ? (this.scene?.overviewFocus?.() ?? null) : null;
    this.viewFocus.poll(focus, Date.now());
  }

  /** Test hook / diagnostics: the Overview focus last told to the street room and how many were sent. */
  viewFocusState(): { lastSent: { x: number; z: number; radius: number } | null | undefined; sent: number } {
    return { lastSent: this.viewFocus.lastSent, sent: this.viewFocus.sent };
  }

  private wireRealtime(): void {
    const ev = this.realtime.events;
    const S = () => this.store.getState();
    ev.on("status", (status, ctx) => {
      S().setRoom({
        status,
        kind: ctx?.kind ?? null,
        roomKey: ctx?.roomKey ?? null,
        districtId: ctx?.districtId ?? null,
        propertyId: ctx?.propertyId ?? null,
        presentationVersion: ctx?.presentationVersion ?? null,
        sessionId: ctx?.sessionId ?? null,
        joinedAt: ctx?.joinedAt ?? null,
        chatDwellSeconds: ctx?.chatDwellSeconds ?? 0,
        chatMinIntervalMs: ctx?.chatMinIntervalMs ?? 0,
        // ADR-0043: the current membership's own people count (null until its state says).
        peopleCount: this.realtime.peopleCount,
        // ADR-0043: the world copy of this membership (the street's copy; inside, the copy the visitor came from).
        worldCopy: ctx?.worldCopy ?? null,
      });
      if (status === "connecting" || status === "transitioning") {
        // A new membership is coming: the next authoritative state re-seeds prediction and the camera.
        this.loop.stop();
        this.predictor = null;
        this.clearDriving();
        this.clearSeating();
        this.input.reset();
        this.snapPending = true;
        this.clearInteractionTimer();
        if (status === "transitioning") S().setUi({ transitionPending: true });
      } else if (status === "joined") {
        // Measurement candidates are posted once the visitor is in a room (the server drops them without a live
        // presence); a panel opened from a link before the join waits in the queue instead of being lost.
        this.analytics.start();
        this.clearInteractionTimer();
        S().setRoomProblem(null);
        S().setDraining(null);
        S().setUi({ transitionPending: false, nearbyEntranceId: null });
        this.onRoomJoined(ctx);
      } else if (status === "closed" || status === "idle") {
        this.clearInteractionTimer();
        this.loop.stop();
        this.predictor = null;
        this.clearDriving();
        this.clearSeating();
        this.input.reset();
        S().setUi({
          transitionPending: false,
          nearbyEntranceId: null,
          realEstateOpen: false,
          homeBuildersOpen: false,
          furnishingsOpen: false,
        });
      }
    });
    ev.on("closed", (info) => {
      S().setRoomProblem({ state: info.state, title: info.title, message: info.message, retryable: info.retryable, code: info.code });
      S().replaceActors([]);
    });
    ev.on("joinFailed", (failure) => {
      // ADR-0043: the destination of a switch between copies refused the join (it filled up meanwhile): a refusal in the
      // Worlds dialog, never a silent move to another copy. The visitor stays where they were.
      const switching = S().ui.worldSwitch;
      if (switching.status === "pending") {
        S().setUi({ transitionPending: false });
        const name = switching.copy !== null ? worldCopyName(switching.copy) : "That world";
        this.endWorldSwitch(
          failure.state === "room_full"
            ? { code: "ROOM_FULL", message: `${name} is full right now. Choose another world or try again in a minute.` }
            : { code: "NOT_AVAILABLE", message: failure.message || "The switch did not work. Try again." },
        );
        if (failure.state !== "incompatible_client") return;
      }
      // A move that could not be completed leaves the visitor where they were.
      S().setUi({ transitionPending: false });
      this.pendingFocusId = null;
      this.pendingDistrictFrame = null;
      if (this.pendingNotice) {
        // The move itself failed, but its reason (e.g. the owner asked visitors to leave) still matters.
        S().pushToast(this.pendingNotice, "warning");
        this.pendingNotice = null;
      }
      if (failure.state === "incompatible_client") {
        // ADR-0036: an outdated tab can only reload; the banner says so (with a Reload button) even while the current
        // room keeps working, instead of a toast that disappears.
        const info = describeClose(CLOSE_CODES.INCOMPATIBLE_CLIENT);
        S().setRoomProblem({ state: info.state, title: info.title, message: failure.message, retryable: false, code: info.code });
      } else if (this.realtime.context) S().pushToast(failure.message, "warning");
      else S().setRoomProblem({ state: failure.state, title: "Couldn't join the town", message: failure.message, retryable: failure.retryable });
    });
    ev.on("actorUpsert", (v) => {
      this.actorVisuals.set(v.actorId, v);
      if (v.isLocal) {
        this.localVisual = { ...this.localVisual, label: v.label, countryCode: v.countryCode, appearance: v.appearance };
        // While prediction runs, the local position comes from it; only the first upsert uses the server's.
        this.scene?.upsertActor(this.predictor ? this.localVisual : { ...this.localVisual, x: v.x, z: v.z, facing: v.facing, anim: v.anim });
      } else {
        this.scene?.upsertActor(v);
      }
      S().upsertActorSummary({ actorId: v.actorId, label: v.label, countryCode: v.countryCode, isLocal: v.isLocal });
    });
    // ADR-0043: on the street an actor also leaves the state by walking out of this tab's view; the client cannot tell
    // that from leaving, so this is quiet (no chat line, no toast). An open person card stays, marked "not nearby".
    ev.on("actorRemove", (actorId) => {
      this.actorVisuals.delete(actorId);
      this.scene?.removeActor(actorId);
      S().removeActorSummary(actorId);
    });
    ev.on("peopleCount", (count) => S().setRoom({ peopleCount: count }));
    ev.on("localActor", (actorId) => {
      this.scene?.setLocalActor(actorId);
      S().setLocalActorId(actorId);
    });
    ev.on("localState", (state) => {
      // One predictor per room membership: a state from a different membership (session id) than the
      // one the predictor was built for re-seeds it (its colliders, bounds and input sequence differ).
      const sessionId = this.realtime.context?.sessionId ?? null;
      const vehicleId = state.vehicleId ?? "";
      if (!this.predictor || this.predictorSessionId !== sessionId) {
        this.predictorSessionId = sessionId;
        this.driveSeqFloor = 0;
        this.predictor = this.makePredictor();
        this.predictor.reset(state);
        this.loop.start();
        this.syncDriving(vehicleId);
        this.syncLocalSeat(state);
        if (!this.drivingVehicleId) this.onLocalTick(this.predictor.state, false);
        if (this.arrivalDistrictPending && this.realtime.context?.kind === "street") {
          this.arrivalDistrictPending = false;
          this.followDistrictOf(state.x, state.z);
        }
      } else if (!this.drivingVehicleId && !vehicleId && ((state.posture ?? 0) !== 0 || this.localSeat)) {
        // Sitting or lying (ADR-0018): the server holds the pose, and standing up is its move to the stand-up spot. No
        // walking is predicted or replayed meanwhile.
        this.predictor.reset(state);
        this.syncLocalSeat(state);
        this.onLocalTick(this.predictor.state, false);
      } else if (this.drivingVehicleId || vehicleId) {
        // Driving (the server holds the actor in the driver's seat) or getting in / out: no walking
        // replay; the walking prediction restarts from the authoritative spot (e.g. the exit point).
        const wasDriving = this.drivingVehicleId !== null;
        this.predictor.reset(state);
        this.syncDriving(vehicleId);
        if (wasDriving && !this.drivingVehicleId) this.onLocalTick(this.predictor.state, false);
      } else {
        this.predictor.reconcile(state);
      }
      this.maybeSnapCamera();
    });
    ev.on("vehicleUpsert", (v) => this.onVehicleState(v));
    ev.on("vehicleRemove", (vehicleId) => this.onVehicleRemoved(vehicleId));
    ev.on("vehicleResult", (r) => this.onVehicleResult(r));
    ev.on("seatResult", (r) => this.onSeatResult(r));
    // Attractions (ADR-0016): the venue game's state is the server's; the overlay and the 3D only display it.
    ev.on("venueState", (m) => {
      const before = useVenueStore.getState().seq;
      useVenueStore.getState().applyState(m.venue, m.seq, m.state);
      if (useVenueStore.getState().seq !== before) {
        this.scene?.updateVenue?.(m.state);
        this.onVenueShared(m.state);
      }
    });
    ev.on("venuePrivate", (m) => useVenueStore.getState().applyPrivate(m.venue, m.chips, m.state));
    ev.on("venueEvent", (m) => {
      useVenueStore.getState().pushEvent(m);
      if (m.type === "rejected") S().pushToast(m.message, "warning");
    });
    ev.on("welcome", (w) => {
      // A new room (not a reconnect to the same venue) starts with an empty venue state.
      const venue = w.roomKind === "interior" ? venueOf(this.deps.manifest, w.propertyId) : null;
      if (useVenueStore.getState().venue !== venue) useVenueStore.getState().reset(venue);
      S().setRoom({
        roomKey: w.roomKey,
        districtId: w.districtId ?? null,
        propertyId: w.propertyId ?? null,
        presentationVersion: w.presentationVersion ?? null,
        chatDwellSeconds: w.chatDwellSeconds,
        chatMinIntervalMs: w.chatMinIntervalMs,
        worldCopy: this.realtime.context?.worldCopy ?? null,
      });
      this.applyRoomToScene();
    });
    // ADR-0043: a switch between world copies the room refused (the visitor stays where they are).
    ev.on("worldSwitchRefused", (r) => this.endWorldSwitch({ code: r.code, message: r.message }));
    ev.on("chatMessage", (m) => {
      S().addChatMessage(m, m.actorId === this.deps.bootstrap.actor.actorId);
      // Speech bubble above the speaker (AI residents and people alike); the text is already server-filtered.
      // Not for people this visitor muted (ADR-0013).
      if (!(m.actorId in this.store.getState().mutes)) this.scene?.showSpeech?.(m.actorId, m.text);
    });
    ev.on("chatRejected", (r) => S().setChatRejection(r));
    // ADR-0014: a private message arrived for this visitor; the messages UI re-reads the API.
    ev.on("dmNotice", (n) => S().pingDm(n.conversationId));
    ev.on("chatRemoved", (id) => S().removeChatMessage(id));
    ev.on("moderation", (n) => {
      this.lastModerationAt = Date.now();
      S().pushToast(n.message, n.code === "WARNING" || n.code === "MUTED" || n.code === "KICKED" ? "warning" : "info");
      S().addSystemChat(n.message);
    });
    ev.on("propertyChanged", (p) => {
      void this.refreshProperty(p.propertyId, { announce: true });
      // Someone bought a plot or changed a home: listings, (street) colliders and yard items change with it.
      if (getPlot(this.deps.manifest, p.propertyId)?.type === "RESIDENTIAL") {
        const qc = this.deps.queryClient;
        void qc.invalidateQueries({ queryKey: queryKeys.realEstateListings });
        // My own home changed (e.g. from another tab), or a resident without a home may just have bought
        // this plot elsewhere: the manager re-reads it; the server stays authoritative.
        const mine = qc.getQueryData<MyHomeResponse>(queryKeys.myHome);
        if (mine?.home?.propertyId === p.propertyId || (mine && mine.home === null)) {
          void qc.invalidateQueries({ queryKey: queryKeys.myHome });
          void qc.invalidateQueries({ queryKey: queryKeys.inventory });
        }
      }
    });
    ev.on("walletChanged", () => {
      // Invalidation only: balances are always re-read from the API, never taken from the room.
      void this.deps.queryClient.invalidateQueries({ queryKey: queryKeys.wallets });
    });
    ev.on("transitionStarted", (t) => {
      this.lastTransition = { roomName: t.roomName, propertyId: t.propertyId, districtId: t.districtId, serverInitiated: t.serverInitiated, at: Date.now() };
      if (t.notice) this.pendingNotice = t.notice;
      // ADR-0043: the server accepted a switch between copies: its ticket names the copy (a friend join learns it here).
      const switching = S().ui.worldSwitch;
      if (switching.status === "pending" && switching.copy === null && t.roomName === "street" && t.worldCopy !== undefined) {
        S().setUi({ worldSwitch: { ...switching, copy: t.worldCopy } });
      }
    });
    ev.on("transitionFailed", (f) => {
      this.clearInteractionTimer();
      // A refused move leaves the visitor where they are.
      S().setUi({ transitionPending: false });
      this.pendingFocusId = null;
      this.pendingDistrictFrame = null;
      S().pushToast(f.message || `Couldn't enter (${f.code}).`, "warning");
    });
    ev.on("draining", (d) => S().setDraining(d));
    ev.on("degraded", (m) => S().setDegraded(m));
    ev.on("controlChanged", () => S().addSystemChat("Townsy was opened in another tab."));
    ev.on("dropped", () => {
      S().pushToast("Connection lost — reconnecting…", "warning");
      // Nothing reaches the server meanwhile: do not predict a drive it never sees.
      this.drive?.hold(true);
      this.venueDrive?.hold(true);
    });
    ev.on("reconnected", () => {
      S().pushToast("Reconnected.", "info");
      this.snapPending = true;
      // Drive on from the server's pose (unless "Get out" is still being answered).
      if (S().ui.vehiclePending !== "exit") this.releaseHold();
    });
  }

  private onRoomJoined(ctx: RoomContext | null): void {
    // Every join updates the current district: an interior's plot's; on the street, the square the ticket asked for
    // until the avatar's first position says where it is (town v6: one street, the district follows the position).
    // The realtime client reports "joined" twice for one entry: when the room is joined, and again when its welcome
    // arrives with the room key. The second report is the same membership (same session), not a new entry: it keeps the
    // chat and the entry line chosen the first time. A restored membership is a new report of its own.
    const sameMembership = Boolean(ctx && !ctx.restored && ctx.sessionId === this.lastJoinedSessionId);
    this.lastJoinedSessionId = ctx?.sessionId ?? null;
    if (!sameMembership) this.setCurrentDistrict(this.roomDistrictOf(ctx));
    if (ctx?.kind === "street" && this.predictor) this.followDistrictOf(this.localVisual.x, this.localVisual.z);
    const districtId = this.currentDistrictId;
    this.refreshSeats();
    const S = this.store.getState();
    S.replaceActors(
      this.realtime.getActors().map((a) => ({ actorId: a.actorId, label: a.label, countryCode: a.countryCode, isLocal: a.isLocal })),
    );
    if (!sameMembership) S.clearChat();
    const kind = ctx?.kind === "interior" && ctx.propertyId ? this.civicKind(ctx.propertyId) : null;
    const inOffice = kind === "real_estate_office";
    const inHomeBuilders = kind === "home_builders";
    const inFurnishings = kind === "furnishings";
    if (ctx?.kind === "interior" && ctx.propertyId) {
      const property = S.properties[ctx.propertyId];
      const name =
        property?.placement?.businessName ?? property?.civic?.name ?? getPlot(this.deps.manifest, ctx.propertyId)?.address ?? "the building";
      if (!sameMembership) S.addSystemChat(ctx.restored ? `Reconnected inside ${name}.` : `You entered ${name}. Chat here stays inside this room.`);
      // A one-room interior is explored on foot; the overview camera has nothing to frame inside.
      if (S.ui.mode === "overview") this.setMode("walk");
      if (inOffice) this.prefetchListings();
      if (inHomeBuilders || inFurnishings) this.prefetchCatalog();
    } else if (!sameMembership) {
      S.addSystemChat(this.streetWelcomeLine(districtId, ctx?.restored ?? false));
      // ADR-0043: a switch between copies arrived (after the street's own line: which copy this is).
      if (ctx?.kind === "street") this.completeWorldSwitch(ctx.worldCopy);
    }
    // Each Townsy shop's panel opens by itself inside that shop and closes everywhere else.
    const panels: Partial<UiState> = {};
    if (S.ui.realEstateOpen !== inOffice) panels.realEstateOpen = inOffice;
    if (S.ui.homeBuildersOpen !== inHomeBuilders) panels.homeBuildersOpen = inHomeBuilders;
    if (S.ui.furnishingsOpen !== inFurnishings) panels.furnishingsOpen = inFurnishings;
    // Arriving in a shop shows that shop; the My home manager can be reopened from the HUD.
    if ((inOffice || inHomeBuilders || inFurnishings) && S.ui.myHomeOpen) panels.myHomeOpen = false;
    if (Object.keys(panels).length > 0) S.setUi(panels);
    // An attraction has its own controls at the bottom of the screen (ADR-0016): the building's panel would cover its big
    // views (founder 2026-10-13: the blackjack table view), so it closes on arrival.
    if (ctx?.kind === "interior" && venueOf(this.deps.manifest, ctx.propertyId) && S.ui.selectedPropertyId) this.deps.navigate("/");
    this.announceServerMove();
    this.applyRoomToScene();
    this.maybeSnapCamera();
    if (ctx?.kind === "street" && this.pendingFocusId) {
      const id = this.pendingFocusId;
      this.pendingFocusId = null;
      this.pendingDistrictFrame = null;
      this.setMode("overview");
      this.focusProperty(id);
    } else if (ctx?.kind === "street" && this.pendingDistrictFrame) {
      const id = this.pendingDistrictFrame;
      this.pendingDistrictFrame = null;
      this.setMode("overview");
      this.scene?.frameDistrict(id);
    }
  }

  // ---------------------------------------------------------------------------------------------
  // Named world copies (ADR-0043): the Worlds list's switch and "Join them in Finch"
  // ---------------------------------------------------------------------------------------------

  private worldSwitchTimer: ReturnType<typeof setTimeout> | null = null;

  /** The world copy the visitor is in (the street's; inside, the copy they came from), or null before the room says. */
  get worldCopy(): number | null {
    return this.realtime.context?.worldCopy ?? null;
  }

  /**
   * Move to world copy `copy` (the Worlds list), arriving at the same spot of the town (inside a building: its door). The
   * current room checks the copy and issues the ticket; the dialog shows pending, then the result or the refusal.
   */
  switchWorld(copy: number): boolean {
    return this.startWorldSwitch({ copy }, { copy, friendActorId: null });
  }

  /**
   * "Join them in Finch" (person card, conversations): the room checks the friend's privacy setting and finds their copy
   * (arriving on its arrival square, never at their position; from inside a building, at its door).
   */
  joinFriend(friendActorId: string): boolean {
    return this.startWorldSwitch({ friendActorId }, { copy: null, friendActorId });
  }

  private startWorldSwitch(request: { copy: number } | { friendActorId: string }, pending: { copy: number | null; friendActorId: string | null }): boolean {
    const S = this.store.getState();
    if (S.ui.worldSwitch.status === "pending" || S.ui.transitionPending) return false;
    if (!this.realtime.requestWorldSwitch(request)) {
      S.setUi({ worldSwitch: { status: "refused", copy: pending.copy, code: "NOT_AVAILABLE", message: "You can't switch worlds right now. Try again in a moment." } });
      return false;
    }
    S.setUi({ worldSwitch: { status: "pending", ...pending } });
    if (this.worldSwitchTimer) clearTimeout(this.worldSwitchTimer);
    this.worldSwitchTimer = setTimeout(() => {
      this.worldSwitchTimer = null;
      if (this.store.getState().ui.worldSwitch.status !== "pending" || this.realtime.isTransitioning) return;
      this.realtime.clearPendingInteraction();
      this.endWorldSwitch({ code: "NOT_AVAILABLE", message: "That took too long. Try again." });
    }, INTERACTION_TIMEOUT_MS);
    return true;
  }

  /** The switch was refused: the dialog shows why (the visitor stays where they are). */
  private endWorldSwitch(refusal: { code: string; message: string }): void {
    if (this.worldSwitchTimer) clearTimeout(this.worldSwitchTimer);
    this.worldSwitchTimer = null;
    const S = this.store.getState();
    const current = S.ui.worldSwitch;
    const copy = current.status === "idle" ? null : current.copy;
    S.setUi({ worldSwitch: { status: "refused", copy, code: refusal.code, message: refusal.message } });
  }

  /** A new street membership: a pending switch arrived (the dialog closes and the visitor is told where they are). */
  private completeWorldSwitch(copy: number | null): void {
    const S = this.store.getState();
    if (S.ui.worldSwitch.status !== "pending") return;
    if (this.worldSwitchTimer) clearTimeout(this.worldSwitchTimer);
    this.worldSwitchTimer = null;
    S.setUi({ worldSwitch: { status: "idle" }, worldsOpen: false });
    if (copy !== null) {
      const name = worldCopyName(copy);
      S.pushToast(`You're in ${name} now.`, "success");
      S.addSystemChat(`You're in ${name}, a copy of the same town: the shops, signs and owners are the same in every world.`);
    }
  }

  /** Open or close the Worlds dialog (a closed dialog forgets an old refusal). */
  setWorldsOpen(open: boolean): void {
    const S = this.store.getState();
    const patch: Partial<UiState> = { worldsOpen: open };
    if (!open && S.ui.worldSwitch.status === "refused") patch.worldSwitch = { status: "idle" };
    S.setUi(patch);
  }

  /**
   * The system chat line of a street join (town v6: one street for the whole town, its chat visible to everyone on it).
   * Central (the first district) keeps its line ("You are on Main Street…"); a join at another district's square names
   * that district.
   */
  private streetWelcomeLine(districtId: string, restored: boolean): string {
    const first = this.deps.manifest.districts[0]!.id;
    const name = this.districtName(districtId);
    if (restored) return districtId === first ? "Reconnected to Main Street." : `Reconnected to ${name}.`;
    if (districtId === first) return "You are on Main Street. Chat here is visible to everyone on the street.";
    return `You're in ${name}. Chat here is visible to everyone on the street.`;
  }

  /**
   * After a server-initiated move: show the reason it gave once the move completes. (ADR-0019: closing a home no longer
   * moves anyone; the owner's "Send visitors out" arrives as a moderation notice before the ticket, so there is nothing
   * to guess from the property afterwards.)
   */
  private announceServerMove(): void {
    const S = this.store.getState();
    const notice = this.pendingNotice;
    this.pendingNotice = null;
    if (notice) {
      if (Date.now() - this.lastModerationAt > NOTICE_DEDUPE_MS) S.pushToast(notice, "warning");
      S.addSystemChat(notice);
    }
  }

  private makePredictor(): Predictor {
    const m = this.deps.manifest;
    const ctx = this.realtime.context;
    const room = ctx ?? { kind: "street" as const, districtId: this.districtId, propertyId: null };
    const inputs = environmentInputs(room, this.allProperties());
    this.colliderKey = inputs.key;
    const env = predictionEnvironment(m, room, inputs.owned, inputs.decor);
    const predictor = new Predictor(m.movement, env.colliders, env.bounds);
    // Walkers collide with every car (ADR-0006), exactly as the server passes them to stepMotion.
    predictor.setObstacleSource(() => (this.vehicles.size ? carObstacles(this.vehicles.values()) : []));
    return predictor;
  }

  private onLocalTick(state: { x: number; z: number; facing: number; anim: 0 | 1 | 2 }, moving: boolean): void {
    const anim = moving ? (state.anim === 2 ? 2 : 1) : state.anim;
    this.localVisual = { ...this.localVisual, x: state.x, z: state.z, facing: state.facing, anim, posture: this.localPostureVisual() };
    this.scene?.upsertActor(this.localVisual);
    this.updateVehicleCandidate(state);
    this.updateSeatOffer(state);
    // Town v6 (ADR-0042): the district follows where the avatar stands.
    this.followDistrictOf(state.x, state.z);
    for (const listener of this.localTickListeners) listener({ x: state.x, z: state.z });
    this.tickCount++;
    if (this.tickCount % 5 !== 0) return;
    const S = this.store.getState();
    const ctx = this.realtime.context;
    let nearby: string | null = null;
    // Doors are for people on their feet (ADR-0018): no Enter prompt while sitting or lying.
    if (S.ui.mode === "walk" && ctx?.kind === "street" && !this.localSeat) {
      // Every district's doors (town v6: one street).
      const plot = nearestEntrance(this.deps.manifest, { x: state.x, z: state.z }, this.deps.manifest.movement.entranceRadiusMetres);
      // Empty lots have no door (they are open land with a For Sale sign).
      if (plot && !(plot.type === "RESIDENTIAL" && S.properties[plot.id]?.status !== "OWNED")) nearby = plot.id;
    }
    if (nearby !== S.ui.nearbyEntranceId) S.setUi({ nearbyEntranceId: nearby });
  }

  /**
   * Re-read one property from the API (after `property.changed`, a purchase or a board edit),
   * update the store and the renderer. Resolves with the fresh property (null on failure).
   */
  async refreshProperty(propertyId: string, opts: { announce?: boolean } = {}): Promise<PropertyPublic | null> {
    const before = this.store.getState().properties[propertyId] ?? null;
    try {
      const item = await this.deps.queryClient.fetchQuery({
        queryKey: queryKeys.property(propertyId),
        queryFn: () => fetchProperty(propertyId),
        staleTime: 0,
      });
      this.store.getState().upsertProperty(item);
      this.deps.queryClient.setQueryData(queryKeys.property(propertyId), item);
      void this.deps.queryClient.invalidateQueries({ queryKey: queryKeys.properties });
      if (before && before.ownershipIntervalId !== item.ownershipIntervalId) {
        // A new owner: "current owner" statistics start over; skip the 30 s HTTP cache of the stats route.
        markPropertyStatsStale(propertyId);
        void this.deps.queryClient.invalidateQueries({ queryKey: queryKeys.propertyStats(propertyId) });
      }
      void this.deps.queryClient.invalidateQueries({ queryKey: queryKeys.myPermissions(propertyId) });
      this.updatePlacements();
      const ctx = this.realtime.context;
      if (opts.announce && ctx?.kind === "interior" && ctx.propertyId === propertyId && before) this.announceChange(before, item);
      return item;
    } catch (err) {
      console.warn("[town] property refresh failed", err);
      return null;
    }
  }

  /** Occupants are told what changed; the room itself stays the same (one room per property). */
  private announceChange(before: PropertyPublic, after: PropertyPublic): void {
    const S = this.store.getState();
    // A board edit also bumps presentationVersion (API), so branding is compared by what is rendered.
    if (before.ownershipIntervalId !== after.ownershipIntervalId) {
      S.pushToast("This building just changed owner. You are still in the same room.", "info");
      S.addSystemChat("This building just changed owner.");
    } else if (placementBrandingKey(before.placement) !== placementBrandingKey(after.placement)) {
      S.pushToast("This building's branding was updated. You are still in the same room.", "info");
      S.addSystemChat("This building's branding was updated.");
    } else if (before.interiorTheme !== after.interiorTheme) {
      S.pushToast("The owner changed this store's interior. You are still in the same room.", "info");
      S.addSystemChat("The owner changed this store's interior.");
    } else if ((before.interiorBoard?.version ?? 0) !== (after.interiorBoard?.version ?? 0)) {
      S.addSystemChat("The store board was updated.");
    }
  }

  // ---------------------------------------------------------------- commands

  setMode(mode: CameraMode): void {
    this.store.getState().setMode(mode);
    this.scene?.setMode(mode);
    this.input.reset();
    if (mode === "overview") this.store.getState().setUi({ nearbyEntranceId: null });
  }

  setQuality(q: QualityLevel): void {
    this.store.getState().setQuality(q);
    this.scene?.setQuality(q);
  }

  setReducedMotion(enabled: boolean): void {
    this.store.getState().setUi({ reducedMotion: enabled });
    this.scene?.setReducedMotion(enabled);
  }

  /** Scene click/tap: selection is route-driven so `/place/:id` and the panel stay in sync. */
  /**
   * A selection that came from clicking the 3D view. The visitor is already looking at that building,
   * so the camera must not fly to it: that would fight the double-click zoom (founder feedback item 1).
   * Directory clicks, deep links and "Go there" still focus the camera.
   */
  private sceneSelectionId: string | null = null;

  private selectFromScene(id: string | null): void {
    const current = this.store.getState().ui.selectedPropertyId;
    if (id === current) return;
    this.sceneSelectionId = id;
    this.deps.navigate(id ? `/place/${id}` : "/");
  }

  focusProperty(id: string | null): void {
    if (!this.scene) return;
    this.scene.highlightProperty(id);
    const fromScene = id !== null && id === this.sceneSelectionId;
    this.sceneSelectionId = null;
    if (fromScene) return;
    if (id && this.store.getState().ui.mode === "overview") this.scene.focusProperty(id);
  }

  resetOverview(): void {
    this.scene?.resetOverview();
  }

  private armInteractionTimer(): void {
    this.clearInteractionTimer();
    this.interactionTimer = setTimeout(() => {
      this.interactionTimer = null;
      const S = this.store.getState();
      if (!S.ui.transitionPending || this.realtime.isTransitioning) return;
      this.realtime.clearPendingInteraction();
      S.setUi({ transitionPending: false });
      S.pushToast("The door didn't respond. Try again.", "warning");
    }, INTERACTION_TIMEOUT_MS);
  }

  private clearInteractionTimer(): void {
    if (this.interactionTimer) clearTimeout(this.interactionTimer);
    this.interactionTimer = null;
  }

  /**
   * Enter a property: in Walk mode near the entrance the Enter button asks the server (the same as
   * walking through the door, which the server detects by itself); in Overview it is explicit fast
   * travel through the room-ticket API.
   */
  async enterProperty(id: string): Promise<void> {
    const S = this.store.getState();
    const property = S.properties[id];
    if (!property || !this.canEnter(id)) {
      S.pushToast("This place can't be entered right now.", "warning");
      return;
    }
    if (S.ui.transitionPending) return;
    const ctx = this.realtime.context;
    if (!ctx || this.realtime.connectionStatus !== "joined") {
      S.pushToast("You're not connected to the town yet.", "warning");
      return;
    }
    if (ctx.kind === "interior") {
      S.pushToast("Exit this building first.", "info");
      return;
    }
    if (this.drivingVehicleId) {
      S.pushToast(OUT_OF_CAR_FIRST, "info");
      return;
    }
    if (S.ui.mode === "walk") {
      if (S.ui.nearbyEntranceId === id) {
        if (this.realtime.requestEnter(id, property.version)) {
          S.setUi({ transitionPending: true });
          this.armInteractionTimer();
        }
        return;
      }
      S.pushToast("Walk up to the door to go in, or switch to Overview to travel there.", "info");
      return;
    }
    S.setUi({ transitionPending: true });
    const ok = await this.realtime.joinInterior(id);
    if (!ok) this.store.getState().setUi({ transitionPending: false });
  }

  /**
   * Explicit fast travel into a building from anywhere (street or another interior), e.g. "Go to
   * the Real Estate office". Uses the room-ticket API and the same atomic join-then-leave.
   */
  async fastTravel(id: string): Promise<boolean> {
    const S = this.store.getState();
    const property = S.properties[id];
    if (property && !this.canEnter(id)) {
      S.pushToast("This place can't be entered right now.", "warning");
      return false;
    }
    if (S.ui.transitionPending || this.realtime.isTransitioning) return false;
    const ctx = this.realtime.context;
    if (!ctx || this.realtime.connectionStatus !== "joined") {
      S.pushToast("You're not connected to the town yet.", "warning");
      return false;
    }
    if (this.drivingVehicleId) {
      S.pushToast(OUT_OF_CAR_FIRST, "info");
      return false;
    }
    if (ctx.kind === "interior" && ctx.propertyId === id) {
      const kind = this.civicKind(id);
      if (kind === "real_estate_office") S.setUi({ realEstateOpen: true });
      else if (kind === "home_builders") S.setUi({ homeBuildersOpen: true, myHomeOpen: false });
      else if (kind === "furnishings") S.setUi({ furnishingsOpen: true, myHomeOpen: false });
      return true;
    }
    S.setUi({ transitionPending: true });
    const ok = await this.realtime.joinInterior(id);
    if (!ok) this.store.getState().setUi({ transitionPending: false });
    return ok;
  }

  /**
   * Town v6 (ADR-0042 decisions 4 and 5): frame a district in Overview (its quick "Central" / "Riverside" / "Hillcrest"
   * button): the camera glides to that district's framing (its shopping street, rooftop signs readable). The whole town
   * is one room, so this never travels: from Walk mode it switches to Overview first; from inside a building the visitor
   * leaves through the door first and the Overview frames the district once back on the street. False for a district
   * the manifest does not know.
   */
  frameDistrict(districtId: string): boolean {
    if (!this.knownDistrict(districtId)) return false;
    if (this.realtime.context?.kind === "interior") {
      this.pendingFocusId = null;
      this.pendingDistrictFrame = districtId;
      this.leaveBuilding();
      return true;
    }
    this.setMode("overview");
    return this.scene?.frameDistrict(districtId) ?? false;
  }

  /**
   * ADR-0042 decision 5 ("Go there" frames the place in Overview, same room): show a district, optionally framing one of
   * its places (`focusPropertyId`) instead of the district. The same in either camera mode (Walk switches to Overview;
   * one room: there is no square to teleport to; "Take me there" fast-travels into a place with `fastTravel`). Kept as a
   * promise for the callers written against world v5's street-to-street travel.
   */
  async travelToDistrict(districtId: string, focusPropertyId?: string | null): Promise<boolean> {
    if (!this.knownDistrict(districtId)) return false;
    if (focusPropertyId && this.districtOf(focusPropertyId)) {
      this.goToProperty(focusPropertyId, { navigate: false });
      return true;
    }
    return this.frameDistrict(districtId);
  }

  /** Leave the current building through its door (the server's exit ticket); a failed request joins the street directly. */
  private leaveBuilding(): void {
    if (this.realtime.requestExit()) {
      this.store.getState().setUi({ transitionPending: true });
      this.armInteractionTimer();
    } else {
      void this.realtime.joinStreet(this.districtId, { reconnectFirst: false });
    }
  }

  /**
   * "Go there": open the property and frame it in overview, wherever it is in the town (town v6: one street; from inside
   * a building the visitor leaves through the door first).
   */
  goToProperty(id: string, opts: { navigate?: boolean } = {}): void {
    if (opts.navigate ?? true) this.deps.navigate(`/place/${id}`);
    const ctx = this.realtime.context;
    if (ctx?.kind === "interior") {
      this.pendingFocusId = id;
      this.pendingDistrictFrame = null;
      this.leaveBuilding();
      return;
    }
    this.setMode("overview");
    this.focusProperty(id);
  }

  /** A plot purchase was confirmed by the server: refresh everything it changed and celebrate honestly. */
  afterPlotPurchase(res: PlotPurchaseResponse, address: string): void {
    const qc = this.deps.queryClient;
    void qc.invalidateQueries({ queryKey: queryKeys.wallets });
    void qc.invalidateQueries({ queryKey: queryKeys.realEstateListings });
    void qc.invalidateQueries({ queryKey: queryKeys.properties });
    // The resident now has a home (a tent): the HUD "My home", the shops and the manager re-read it.
    void qc.invalidateQueries({ queryKey: queryKeys.myHome });
    void qc.invalidateQueries({ queryKey: queryKeys.inventory });
    void this.refreshProperty(res.propertyId);
    this.store.getState().pushToast(`You own ${address}. Your home starts as a tent.`, "success", {
      label: "Go there",
      run: () => this.goToProperty(res.propertyId),
    });
  }

  /** Listings/balances went stale during a purchase (price changed, sold out, limit): refetch them. */
  refreshAfterPurchaseChange(): void {
    const qc = this.deps.queryClient;
    void qc.invalidateQueries({ queryKey: queryKeys.realEstateListings });
    void qc.invalidateQueries({ queryKey: queryKeys.wallets });
  }

  // ---------------------------------------------------------------- homes (ADR-0005)

  /** Go to Townsy Home Builders / Furnishings (or show its panel when already inside). */
  openShop(shop: "home_builders" | "furnishings"): void {
    const id = shop === "home_builders" ? this.homeBuildersId : this.furnishingsId;
    if (!id) return;
    this.store.getState().setUi({ myHomeOpen: false });
    void this.fastTravel(id);
  }

  openMyHome(tab?: "yard" | "interior"): void {
    const S = this.store.getState();
    S.setUi({ myHomeOpen: true, myHomeTab: tab ?? S.ui.myHomeTab, menuOpen: false });
    // Mobile shows one bottom sheet at a time (docs/01 section 6): close the property sheet.
    if (typeof window !== "undefined" && window.innerWidth <= 900 && S.ui.selectedPropertyId) this.deps.navigate("/");
  }

  /**
   * Frame a home's yard in the overview camera (placing yard items), in any district (town v6: one street). From inside
   * a building the avatar first leaves through the door; the camera frames the home once back on the street.
   */
  showYard(propertyId: string): void {
    const ctx = this.realtime.context;
    if (ctx?.kind === "interior") {
      this.pendingFocusId = propertyId;
      this.pendingDistrictFrame = null;
      this.leaveBuilding();
      return;
    }
    this.setMode("overview");
    this.scene?.focusProperty(propertyId);
  }

  /** Fast travel into a home (the owner placing furniture: "Go inside"). */
  enterHome(propertyId: string): Promise<boolean> {
    return this.fastTravel(propertyId);
  }

  /** The renderer side of the My home manager: the uncommitted ghost and the anchor markers. */
  readonly previewSink: PreviewSink = {
    setDecorationPreview: (preview) => {
      this.decorationPreview = preview ? { ...preview } : null;
      try {
        this.scene?.setDecorationPreview(preview);
      } catch (err) {
        console.warn("[scene] setDecorationPreview failed", err);
      }
    },
    highlightAnchors: (propertyId, scope, anchorIds) => {
      this.anchorHighlight = anchorIds.length > 0 ? { propertyId, scope, anchorIds: [...anchorIds] } : null;
      try {
        this.scene?.highlightAnchors(propertyId, scope, anchorIds);
      } catch (err) {
        console.warn("[scene] highlightAnchors failed", err);
      }
    },
  };

  /**
   * A home change was confirmed by the server (look, open / closed, placement, removal, reset, upgrade):
   * show the confirmed state at once, then re-read the authoritative property, which also rebuilds
   * prediction colliders and updates the renderer (yard items on the street, furniture inside).
   */
  afterHomeChanged(propertyId: string, state: HomeStateResponse | null): void {
    const apply = (base: PropertyPublic | undefined) => {
      if (!state || !base?.home || base.home.layoutVersion > state.layoutVersion) return false;
      const { stage, layoutVersion, access, look, placedItems } = state;
      // ADR-0019: the Open / Closed switch is property-level public data (`access`); `enterable` follows with the re-read.
      this.store.getState().upsertProperty({ ...base, access, home: { ...base.home, stage, layoutVersion, look, placedItems } });
      return true;
    };
    if (apply(this.store.getState().properties[propertyId])) this.updatePlacements();
    void this.refreshProperty(propertyId).then((item) => {
      // Never let a read that raced the change roll the layout back.
      if (item?.home && state && item.home.layoutVersion < state.layoutVersion && apply(item)) this.updatePlacements();
    });
  }

  /** The owner saved the store board: show it immediately, then confirm with the authoritative property. */
  applySavedBoard(propertyId: string, board: InteriorBoard): void {
    const existing = this.store.getState().properties[propertyId];
    if (existing && (existing.interiorBoard?.version ?? 0) <= board.version) {
      this.store.getState().upsertProperty({ ...existing, interiorBoard: board });
      this.updatePlacements();
    }
    void this.refreshProperty(propertyId).then((item) => {
      // Never let a read that raced the save roll the board back.
      if (item && (item.interiorBoard?.version ?? 0) < board.version) {
        this.store.getState().upsertProperty({ ...item, interiorBoard: board });
        this.updatePlacements();
      }
    });
  }

  /**
   * The owner chose a store theme (PUT confirmed it): show it at once (the room swaps it in place
   * when we are inside), then re-read the authoritative property. A read that raced the change
   * never rolls the theme back.
   */
  applySavedTheme(propertyId: string, res: InteriorThemeUpdateResponse): void {
    const qc = this.deps.queryClient;
    const patch = (p: PropertyPublic | undefined) => (p && p.interiorTheme !== res.interiorTheme ? { ...p, interiorTheme: res.interiorTheme } : null);
    const stored = patch(this.store.getState().properties[propertyId]);
    if (stored) {
      this.store.getState().upsertProperty(stored);
      this.updatePlacements();
    }
    const cached = patch(qc.getQueryData<PropertyPublic>(queryKeys.property(propertyId)));
    if (cached) qc.setQueryData(queryKeys.property(propertyId), cached);
    void this.refreshProperty(propertyId).then((item) => {
      if (!item || item.interiorTheme === res.interiorTheme || (item.placement?.presentationVersion ?? 0) >= res.version) return;
      const fixed = { ...item, interiorTheme: res.interiorTheme };
      this.store.getState().upsertProperty(fixed);
      qc.setQueryData(queryKeys.property(propertyId), fixed);
      this.updatePlacements();
    });
  }

  // ---------------------------------------------------------------- town map (ADR-0008)

  /** The local avatar, the car it drives and the other cars, for the minimap (street rooms only). */
  minimapSnapshot(): MinimapSnapshot {
    const ctx = this.realtime.context;
    const onStreet = ctx?.kind === "street" && this.store.getState().localActorId !== null;
    if (!onStreet) return { local: null, driving: null, cars: [] };
    const cars: MinimapCar[] = [];
    let driving: MinimapCar | null = null;
    for (const v of this.vehicles.values()) {
      const isLocal = v.vehicleId === this.drivingVehicleId;
      const pose = isLocal && this.drive ? this.drive.visual : v;
      const box = vehicleObb(v.style, pose);
      const car: MinimapCar = {
        vehicleId: v.vehicleId,
        x: pose.x,
        z: pose.z,
        heading: pose.heading,
        halfLength: box.halfLength,
        halfWidth: box.halfWidth,
        driven: isLocal || Boolean(v.driverActorId),
        isLocal,
      };
      if (isLocal) driving = car;
      else cars.push(car);
    }
    const lv = this.localVisual;
    const local = this.predictor || driving ? { x: lv.x, z: lv.z, heading: driving ? driving.heading : lv.facing } : null;
    return { local, driving, cars };
  }

  // ---------------------------------------------------------------- cars (ADR-0006)

  /** True while the local player drives a car. */
  get isDriving(): boolean {
    return this.drivingVehicleId !== null;
  }

  private localActorId(): string {
    return this.realtime.context?.yourActorId ?? this.deps.bootstrap.actor.actorId;
  }

  /**
   * Environment for one drive step / replay: the same inputs the server gives stepVehicle. The
   * static colliders come as the walking predictor's spatial index (same boxes, rebuilt when they
   * change) and `drivable` is always the same array instance, so the world's road outline cache hits.
   */
  private driveEnv(): DriveEnv {
    return {
      // The whole town's roads (town v6: one street; the same array every step, so the world's outline cache hits).
      drivable: townDrivableRects(this.deps.manifest),
      colliders: this.predictor?.colliderIndex ?? NO_COLLIDERS,
      cars: carObstacles(this.vehicles.values(), this.drivingVehicleId),
      walkers: this.realtime.getWalkers(),
      walkerRadius: this.deps.manifest.movement.actorRadiusMetres,
    };
  }

  private onVehicleState(v: VehicleSnapshot): void {
    this.vehicles.set(v.vehicleId, v);
    if (v.vehicleId === this.drivingVehicleId) {
      // Our own car is drawn from prediction (onDriveTick); its state acknowledges our inputs.
      if (!this.drive) this.startDrivePrediction(v);
      else if (this.drive.isHeld) this.drive.follow(v);
      else this.drive.reconcile(v);
      return;
    }
    this.scene?.upsertVehicle(vehicleVisual(v, false));
  }

  private onVehicleRemoved(vehicleId: string): void {
    this.vehicles.delete(vehicleId);
    this.scene?.removeVehicle(vehicleId);
    const S = this.store.getState();
    if (S.ui.vehicleCandidateId === vehicleId) S.setUi({ vehicleCandidateId: null });
    if (this.doorHighlightId === vehicleId) this.setDoorHighlight(null);
    // Our car left the state: stop predicting it; the actor state says when we are on foot again.
    if (vehicleId === this.drivingVehicleId) this.drive = null;
  }

  private startDrivePrediction(car: VehicleSnapshot): void {
    const firstSeq = Math.max(this.driveSeqFloor + 1, this.predictor?.nextSequence ?? 1, car.lastSeq + 1);
    this.drive = new DrivePredictor({
      vehicleId: car.vehicleId,
      style: car.style,
      hz: this.deps.manifest.movement.simulationHz,
      initial: car,
      firstSeq,
      env: () => this.driveEnv(),
    });
    this.onDriveTick();
  }

  /** Follow ActorState.vehicleId: the server decides when we are in a car. */
  private syncDriving(vehicleId: string): void {
    const id = vehicleId || null;
    if (id === this.drivingVehicleId) return;
    if (this.drivingVehicleId) this.endDriving();
    if (id) this.beginDriving(id);
  }

  private beginDriving(vehicleId: string): void {
    const S = this.store.getState();
    this.drivingVehicleId = vehicleId;
    this.input.reset(); // a key held while walking up must not floor the gas
    this.setDoorHighlight(null);
    try {
      this.scene?.setLocalVehicle(vehicleId);
    } catch (err) {
      console.warn("[scene] setLocalVehicle failed", err);
    }
    if (S.ui.vehiclePending === "enter") this.clearVehicleTimer();
    S.setUi({
      drivingVehicleId: vehicleId,
      vehicleCandidateId: null,
      nearbyEntranceId: null,
      vehiclePending: S.ui.vehiclePending === "enter" ? null : S.ui.vehiclePending,
    });
    const car = this.vehicles.get(vehicleId);
    if (car) this.startDrivePrediction(car);
    else this.startVenueDrive(vehicleId);
  }

  /** Venues with vehicles (ADR-0016 truck arena), or null on the street and in every other building. */
  private venueDriving(): VenueDrivingModule | null {
    if (this.realtime.context?.kind !== "interior") return null;
    const v = useVenueStore.getState().venue;
    return v ? (VENUE_CLIENTS[v].driving ?? null) : null;
  }

  /** A venue vehicle: the venue's own prediction, seeded from its latest shared state (retried on the next one). */
  private startVenueDrive(vehicleId: string): void {
    const module = this.venueDriving();
    if (!module || this.venueDrive) return;
    const firstSeq = Math.max(this.driveSeqFloor + 1, this.predictor?.nextSequence ?? 1);
    this.venueDrive = module.startDriver({ vehicleId, shared: useVenueStore.getState().shared, hz: this.deps.manifest.movement.simulationHz, firstSeq, localActorId: this.localActorId() });
    if (this.venueDrive) this.onDriveTick();
  }

  /** A newer venue state: the venue vehicle we drive reconciles with it (or starts predicting once it is known). */
  private onVenueShared(state: unknown): void {
    if (!this.drivingVehicleId || this.drive) return;
    if (this.venueDrive) this.venueDrive.onShared(state);
    else this.startVenueDrive(this.drivingVehicleId);
  }

  private endDriving(): void {
    const id = this.drivingVehicleId;
    if (this.drive) this.driveSeqFloor = Math.max(this.driveSeqFloor, this.drive.lastSentSeq);
    if (this.venueDrive) {
      this.driveSeqFloor = Math.max(this.driveSeqFloor, this.venueDrive.lastSentSeq);
      this.venueDrive.dispose();
      this.venueDrive = null;
    }
    this.drive = null;
    this.drivingVehicleId = null;
    this.predictor?.ensureNextSeq(this.driveSeqFloor + 1);
    this.input.reset();
    try {
      this.scene?.setLocalVehicle(null);
    } catch (err) {
      console.warn("[scene] setLocalVehicle failed", err);
    }
    // The car stays (parked): from now on it is drawn from the server state like any other car.
    const car = id ? this.vehicles.get(id) : undefined;
    if (car) this.scene?.upsertVehicle(vehicleVisual(car, false));
    const S = this.store.getState();
    if (S.ui.vehiclePending === "exit") this.clearVehicleTimer();
    S.setUi({ drivingVehicleId: null, vehiclePending: S.ui.vehiclePending === "exit" ? null : S.ui.vehiclePending });
  }

  /** Leaving the room (transition, reconnect, close): the server parks the car; forget it locally. */
  private clearDriving(): void {
    if (this.drivingVehicleId) this.endDriving();
    this.clearVehicleTimer();
    this.setDoorHighlight(null);
    const S = this.store.getState();
    if (S.ui.vehicleCandidateId !== null || S.ui.vehiclePending !== null || S.ui.drivingVehicleId !== null) {
      S.setUi({ vehicleCandidateId: null, vehiclePending: null, drivingVehicleId: null });
    }
  }

  private setDoorHighlight(vehicleId: string | null): void {
    if (vehicleId === this.doorHighlightId) return;
    this.doorHighlightId = vehicleId;
    try {
      this.scene?.highlightVehicleDoor(vehicleId);
    } catch (err) {
      console.warn("[scene] highlightVehicleDoor failed", err);
    }
  }

  /** Walking: offer "Get in (E)" when the avatar stands at a stopped, free car's driver's door. */
  private updateVehicleCandidate(pos: { x: number; z: number }): void {
    const S = this.store.getState();
    const ctx = this.realtime.context;
    let id: string | null = null;
    if (!this.drivingVehicleId && this.vehicles.size > 0 && S.ui.mode === "walk" && ctx?.kind === "street" && this.realtime.connectionStatus === "joined") {
      id = enterableVehicle(pos, this.vehicles.values())?.vehicleId ?? null;
    } else if (!this.drivingVehicleId && S.ui.mode === "walk" && ctx?.kind === "interior" && this.realtime.connectionStatus === "joined") {
      id = this.venueDriving()?.candidate(useVenueStore.getState().shared, pos) ?? null;
    }
    if (id !== S.ui.vehicleCandidateId) S.setUi({ vehicleCandidateId: id });
    this.setDoorHighlight(id);
  }

  /** Draw the predicted car (and the driver in its seat) after the drive steps of one tick. */
  private onDriveTick(): void {
    const drive = this.drive;
    if (!drive) {
      if (this.venueDrive) this.onVenueDriveTick(this.venueDrive);
      return;
    }
    const pose = drive.visual;
    // ADR-0049: every simulation step since the last tick goes on the renderer's even timeline (nothing is guessed).
    const { steps, restart } = drive.takeDrawnSteps();
    const car = this.vehicles.get(drive.vehicleId);
    if (car) this.scene?.upsertVehicle({ ...vehicleVisual({ ...car, mode: "driven", driverActorId: this.localActorId() }, true, pose), steps, restart });
    const seat = driverSeatPoint(drive.style, pose);
    this.localVisual = { ...this.localVisual, x: seat.x, z: seat.z, facing: pose.heading, anim: 0 };
    this.scene?.upsertActor(this.localVisual);
    // Town v6: driving into Riverside or Hillcrest changes the visitor's district too.
    this.followDistrictOf(seat.x, seat.z);
    const S = this.store.getState();
    // Drivers cannot use doors (ADR-0006 #4): no Enter prompt, no Get in prompt.
    if (S.ui.nearbyEntranceId !== null || S.ui.vehicleCandidateId !== null) S.setUi({ nearbyEntranceId: null, vehicleCandidateId: null });
  }

  /** The venue draws its vehicle itself (from the same prediction); the avatar rides in the driver's seat. */
  private onVenueDriveTick(d: VenueDriver): void {
    const seat = d.seat();
    this.localVisual = { ...this.localVisual, x: seat.x, z: seat.z, facing: seat.facing, anim: 0 };
    this.scene?.upsertActor(this.localVisual);
    const S = this.store.getState();
    if (S.ui.nearbyEntranceId !== null || S.ui.vehicleCandidateId !== null) S.setUi({ nearbyEntranceId: null, vehicleCandidateId: null });
  }

  private armVehicleTimer(): void {
    this.clearVehicleTimer();
    this.vehicleTimer = setTimeout(() => {
      this.vehicleTimer = null;
      const S = this.store.getState();
      if (!S.ui.vehiclePending) return;
      S.setUi({ vehiclePending: null });
      this.releaseHold();
      S.pushToast("The car didn't respond. Try again.", "warning");
    }, VEHICLE_RESULT_TIMEOUT_MS);
  }

  private clearVehicleTimer(): void {
    if (this.vehicleTimer) clearTimeout(this.vehicleTimer);
    this.vehicleTimer = null;
  }

  /** "Get out" did not happen: drive on from the latest authoritative pose. */
  private releaseHold(): void {
    if (this.venueDrive?.isHeld) this.venueDrive.hold(false);
    const drive = this.drive;
    if (!drive?.isHeld) return;
    drive.hold(false);
    const car = this.vehicles.get(drive.vehicleId);
    if (car) drive.follow(car);
  }

  private onVehicleResult(r: VehicleResult): void {
    this.clearVehicleTimer();
    const S = this.store.getState();
    if (S.ui.vehiclePending) S.setUi({ vehiclePending: null });
    if (r.action === "exit" && !r.ok) this.releaseHold();
    // A venue's vehicles (the truck arena) explain themselves in the venue's words; its overlay shows refusals too.
    const venue = this.venueDriving() ? useVenueStore.getState().venue : null;
    const text = venue ? r.message : vehicleResultMessage(r);
    if (venue && !r.ok) useVenueStore.getState().pushEvent({ venue, type: "rejected", code: r.code, message: r.message.slice(0, 200) });
    if (r.ok && r.code === "ENTERED") {
      S.pushToast(S.ui.isTouch ? DRIVING_HELP_TOUCH : text, "info");
      return;
    }
    if (r.ok) {
      if (r.code === "EXITED") S.pushToast(text, "info");
      return;
    }
    S.pushToast(text, "warning");
  }

  // ---------------------------------------------------------------- seats (ADR-0018)

  /**
   * E (and the bottom prompt): get out of the car we drive, stand up when sitting or lying, else get into the car at
   * whose door we stand, else sit or lie down on the seat offered. Cars keep priority, as before seats existed.
   */
  interact(): void {
    if (this.drivingVehicleId) {
      this.exitVehicle();
      return;
    }
    if (this.localSeat) {
      this.standUp();
      return;
    }
    const S = this.store.getState();
    if (S.ui.vehicleCandidateId) {
      this.enterVehicle(S.ui.vehicleCandidateId);
      return;
    }
    if (S.ui.seatOffer) this.sitOn(S.ui.seatOffer.seatId);
  }

  /** Ask the server to seat us (it checks reach, occupancy and that the seat exists in this room now). */
  sitOn(seatId: string): boolean {
    const S = this.store.getState();
    if (this.drivingVehicleId || this.localSeat || S.ui.seatPending || S.ui.transitionPending || S.ui.vehiclePending) return false;
    const seat = this.seatsById.get(seatId);
    if (!seat || this.realtime.connectionStatus !== "joined" || !this.seatRequestAllowed()) return false;
    const action = seat.kind === "lie" ? "lie" : "sit";
    if (!this.realtime.sendSeatAction(action, seatId)) return false;
    this.lastSeatRequestAt = Date.now();
    S.setUi({ seatPending: action });
    this.armSeatTimer();
    return true;
  }

  /** "Stand up" (E, the button, or walking): the server puts us at the seat's stand-up spot. */
  standUp(): boolean {
    const S = this.store.getState();
    if (!this.localSeat || S.ui.seatPending === "stand" || !this.seatRequestAllowed()) return false;
    if (!this.realtime.sendSeatAction("stand")) return false;
    this.lastSeatRequestAt = Date.now();
    S.setUi({ seatPending: "stand" });
    this.armSeatTimer();
    return true;
  }

  private seatRequestAllowed(): boolean {
    return Date.now() - this.lastSeatRequestAt >= SEAT_REQUEST_INTERVAL_MS;
  }

  /** True while the local avatar sits or lies. */
  get isSeated(): boolean {
    return this.localSeat !== null;
  }

  /** Each movement tick while seated: a movement key pressed (after being released once) stands up. */
  private onSeatedTick(moving: boolean): void {
    if (!moving) {
      this.seatedMoveArmed = true;
      return;
    }
    if (this.seatedMoveArmed) this.standUp();
  }

  private onSeatResult(r: SeatResult): void {
    this.clearSeatTimer();
    const S = this.store.getState();
    if (S.ui.seatPending) S.setUi({ seatPending: null });
    if (!r.ok) S.pushToast(r.message, "warning");
  }

  private armSeatTimer(): void {
    this.clearSeatTimer();
    this.seatTimer = setTimeout(() => {
      this.seatTimer = null;
      const S = this.store.getState();
      if (!S.ui.seatPending) return;
      S.setUi({ seatPending: null });
      S.pushToast("The seat didn't respond. Try again.", "warning");
    }, SEAT_RESULT_TIMEOUT_MS);
  }

  private clearSeatTimer(): void {
    if (this.seatTimer) clearTimeout(this.seatTimer);
    this.seatTimer = null;
  }

  /** Follow ActorState.posture / seatId: the server decides when we sit, lie or stand. */
  private syncLocalSeat(state: AuthoritativeState): void {
    const posture = state.posture ?? 0;
    const next = posture === 0 ? null : { kind: posture === 2 ? ("lie" as const) : ("sit" as const), seatId: state.seatId ?? "" };
    const prev = this.localSeat;
    if (prev?.seatId === next?.seatId && prev?.kind === next?.kind) return;
    this.localSeat = next;
    this.seatedMoveArmed = false;
    const place = next ? (this.seatsById.get(next.seatId)?.place ?? null) : null;
    const S = this.store.getState();
    S.setUi({ seated: next ? { kind: next.kind, place } : null, seatOffer: null, nearbyEntranceId: next ? null : S.ui.nearbyEntranceId });
  }

  /** How the local avatar is drawn on its seat (the seat's height from the room's list), or null when standing. */
  private localPostureVisual(): ActorPostureVisual | null {
    const seat = this.localSeat;
    if (!seat) return null;
    const y = this.seatsById.get(seat.seatId)?.y ?? FALLBACK_SEAT_Y[seat.kind];
    return { kind: seat.kind, y, seatId: seat.seatId };
  }

  /** Walking, on foot, standing, in walk mode: offer the nearest free seat within reach ("Sit on the sofa"). */
  private updateSeatOffer(pos: { x: number; z: number }): void {
    const S = this.store.getState();
    let offer: ReturnType<typeof seatOffer> = null;
    if (!this.localSeat && !this.drivingVehicleId && this.seatList.length > 0 && S.ui.mode === "walk" && this.realtime.connectionStatus === "joined") {
      const taken = new Set<string>();
      for (const v of this.actorVisuals.values()) if (!v.isLocal && v.posture) taken.add(v.posture.seatId);
      // ADR-0019: a closed home's yard seats are offered to its owner only.
      const closed = this.closedHomeOwners();
      const me = this.deps.bootstrap.actor.actorId;
      offer = seatOffer(closed.size ? this.seatList.filter((seat) => mayUseSeat(seat, me, closed)) : this.seatList, pos, taken);
    }
    const current = S.ui.seatOffer;
    if ((current?.seatId ?? null) !== (offer?.seatId ?? null)) S.setUi({ seatOffer: offer });
  }

  /**
   * List the current room's seats again when the room or its furniture / yards / theme changed (the same shared function
   * and data as the server). Seated people drawn with an older list get their seat's height.
   */
  private refreshSeats(): void {
    const ctx = this.realtime.context;
    if (!ctx) return;
    const { seats, key } = roomSeats(this.deps.manifest, ctx, this.allProperties());
    const fullKey = `${ctx.kind}|${ctx.districtId ?? ""}|${ctx.propertyId ?? ""}|${key}`;
    if (fullKey === this.seatsKey) return;
    this.seatsKey = fullKey;
    this.seatList = seats;
    this.seatsById = new Map(seats.map((x) => [x.id, x]));
    for (const v of [...this.actorVisuals.values()]) {
      if (v.isLocal || !v.posture) continue;
      const seat = this.seatsById.get(v.posture.seatId);
      if (!seat || seat.y === v.posture.y) continue;
      const next: ActorVisual = { ...v, posture: { ...v.posture, y: seat.y } };
      this.actorVisuals.set(v.actorId, next);
      this.scene?.upsertActor(next);
    }
    if (this.localSeat) {
      const S = this.store.getState();
      S.setUi({ seated: { kind: this.localSeat.kind, place: this.seatsById.get(this.localSeat.seatId)?.place ?? null } });
    }
  }

  /** Leaving the room (transition, reconnect, close): the server frees the seat; forget it locally. */
  private clearSeating(): void {
    this.localSeat = null;
    this.seatsKey = "";
    this.seatList = [];
    this.seatsById = new Map();
    this.clearSeatTimer();
    const S = this.store.getState();
    if (S.ui.seatOffer !== null || S.ui.seated !== null || S.ui.seatPending !== null) S.setUi({ seatOffer: null, seated: null, seatPending: null });
  }

  /** The touch Run toggle (ADR-0018). */
  setRunToggle(on: boolean): void {
    this.store.getState().setUi({ runToggle: on });
  }

  /** E, the "Get in" / "Get out" buttons: get into the car at whose door we stand, or out of ours. */
  interactWithVehicle(): void {
    if (this.drivingVehicleId) {
      this.exitVehicle();
      return;
    }
    const id = this.store.getState().ui.vehicleCandidateId;
    if (id) this.enterVehicle(id);
  }

  /** Ask the server to put us in the driver's seat (it checks the door distance, speed and occupancy). */
  enterVehicle(vehicleId: string): boolean {
    const S = this.store.getState();
    if (this.drivingVehicleId || S.ui.vehiclePending || S.ui.transitionPending) return false;
    const ctx = this.realtime.context;
    if (!ctx || (ctx.kind !== "street" && !this.venueDriving()) || this.realtime.connectionStatus !== "joined") {
      S.pushToast(VEHICLE_RESULT_TEXT.NOT_AVAILABLE, "info");
      return false;
    }
    if (!this.realtime.sendVehicleAction({ action: "enter", vehicleId })) return false;
    S.setUi({ vehiclePending: "enter" });
    this.armVehicleTimer();
    return true;
  }

  /** Ask the server to stop the car and let us out (it brakes first, then picks a free spot by the door). */
  exitVehicle(): boolean {
    const S = this.store.getState();
    if (!this.drivingVehicleId || S.ui.vehiclePending === "exit") return false;
    if (!this.realtime.sendVehicleAction({ action: "exit" })) return false;
    // The server brakes the car itself: stop sending input and follow its pose until the result.
    this.drive?.hold(true);
    this.venueDrive?.hold(true);
    S.setUi({ vehiclePending: "exit" });
    this.armVehicleTimer();
    return true;
  }

  /** A click / tap on a car in the 3D view (reported by renderers that pick cars). */
  private selectVehicleFromScene(vehicleId: string): void {
    if (this.drivingVehicleId) return;
    const S = this.store.getState();
    if (S.ui.mode !== "walk" || this.realtime.context?.kind !== "street") return;
    if (S.ui.vehicleCandidateId === vehicleId) {
      this.enterVehicle(vehicleId);
      return;
    }
    const car = this.vehicles.get(vehicleId);
    if (!car || Math.hypot(car.x - this.localVisual.x, car.z - this.localVisual.z) > 10) return;
    const code = car.driverActorId ? "OCCUPIED" : Math.abs(car.speed) >= 0.3 ? "MOVING" : "TOO_FAR";
    S.pushToast(VEHICLE_RESULT_TEXT[code], "info");
  }

  exitInterior(): void {
    const S = this.store.getState();
    if (S.ui.transitionPending) return;
    if (this.realtime.requestExit()) {
      S.setUi({ transitionPending: true });
      this.armInteractionTimer();
    }
  }

  returnToPlaza(): boolean {
    const S = this.store.getState();
    const now = Date.now();
    if (now < S.ui.returnCooldownUntil) return false;
    if (!this.realtime.returnToPlaza()) return false;
    S.setUi({ returnCooldownUntil: now + RETURN_TO_PLAZA_COOLDOWN_MS });
    return true;
  }

  /** ADR-0053: a key pressed in the chat box (or Say hi's draft): the AI resident talking with this visitor waits. */
  noteChatTyping(): void {
    this.realtime.sendChatTyping();
  }

  sendChat(text: string): boolean {
    const id = clientMessageId();
    const ok = this.realtime.sendChat(text, id);
    if (ok) this.store.getState().markChatSent(Date.now());
    return ok;
  }

  async takeControlHere(): Promise<void> {
    this.store.getState().setRoomProblem(null);
    await this.realtime.takeControlHere();
  }

  async retryJoin(): Promise<void> {
    this.store.getState().setRoomProblem(null);
    if (this.realtime.currentTarget) await this.realtime.rejoin();
    else await this.connect();
  }

  dispose(): void {
    this.disposed = true;
    this.clearInteractionTimer();
    if (this.worldSwitchTimer) clearTimeout(this.worldSwitchTimer);
    this.worldSwitchTimer = null;
    this.clearVehicleTimer();
    this.clearSeatTimer();
    this.loop.stop();
    this.input.detach();
    this.analytics.stop();
    for (const c of this.cleanups) c();
    this.realtime.dispose();
    const scene = this.scene;
    this.scene = null;
    try {
      scene?.dispose();
    } catch {
      /* ignore */
    }
    const S = this.store.getState();
    S.resetRoom();
    S.setUi({
      sceneStatus: "idle",
      transitionPending: false,
      nearbyEntranceId: null,
      realEstateOpen: false,
      homeBuildersOpen: false,
      furnishingsOpen: false,
      myHomeOpen: false,
      walletOpen: false,
      earnOpen: false,
      storeTasksFor: null,
      vehicleCandidateId: null,
      drivingVehicleId: null,
      vehiclePending: null,
      seatOffer: null,
      seated: null,
      seatPending: null,
    });
  }
}
